import React, { Component } from "react";
import firebase from "firebase";
import DB_PATH from "../../../../../../database/DB_PATH";
import { uploadMedia } from "../../../../../../database/media/cloudflareMedia";
import {
  getCommunityReelCreatorConfig,
  saveCommunityReelCreatorConfig,
} from "../../../../../../database/community/reelCreatorConfig";
import { Author } from "../../../../../../types/Author";
import { ReelVoiceAuthorReference } from "../../../../../../types/ReelVoiceAuthorReference";
import { ReelCharacter } from "../../../../../../types/ReelCharacter";
import { ReelNarrationCue, ReelSegment, SlideImageOption } from "./types";
import {
  ViralReelCreatorProps,
  ViralReelCreatorState,
  GenerationSubstep,
  GenerationSubstepStatus,
  ReelFormatPromptTemplateMap,
  VideoFormat,
  ThreeStepValidatorPromptTemplateMap,
} from "./ViralReelCreator.types";
import {
  SEGMENT_COUNT,
  ReelGenerationProgressUpdate,
  applyCharacterAnchor,
  extractCharacterAnchor,
  generateElevenLabsVoice,
  prepareNarrationText,
  generateSeedanceTransitionVideo,
  generateBusinessStoryCandidates,
  generateBusinessStoryScriptsFromCandidate,
  generateReelSegments,
  generateImagePromptsForImportedScripts,
  buildCharacterReferencePrompt,
  generateGeminiImage,
  generateGptImage,
  DEFAULT_REEL_FORMAT_PROMPTS,
  DEFAULT_THREE_STEP_VALIDATOR_PROMPTS,
  getDefaultReelFormatPrompts,
  getDefaultThreeStepValidatorPrompts,
  rewriteBusinessStoryScripts,
  scoreBusinessStoryScripts,
  supportsThreeStepValidator,
  inferCharacterDescription,
  enforceCharacterConsistency,
  ReelCastMember,
  generateDialogueReelSegments,
  generateDialogueImagePrompts,
} from "./ViralReelCreator.api";
import { ViralReelStudioView } from "./ViralReelCreator.studio.view";

const DEFAULT_ANIMATED_SEGMENT_SECONDS = 5;
const MIN_ANIMATED_SEGMENT_SECONDS = 1.5;
const REEL_FRAME_RATE = 30;
const TRANSITION_AUTOSAVE_TIMEOUT_MS = 45000;
const FFMPEG_SCRIPT_URL = "https://cdn.jsdelivr.net/npm/@ffmpeg/ffmpeg@0.11.6/dist/ffmpeg.min.js";
const FFMPEG_CORE_URL = "https://unpkg.com/@ffmpeg/core-st@0.11.1/dist/ffmpeg-core.js";

class ViralReelCreator extends Component<ViralReelCreatorProps, ViralReelCreatorState> {
  state: ViralReelCreatorState = {
    isGenerating: false,
    progress: "",
    progressPercent: 0,
    generationSubsteps: [],
    segments: [],
    currentStep: "idle",
    promptInputs: [],
    scriptInputs: [],
    aiGeneratingIndex: null,
    aiGeneratingAll: false,
    aiGeneratingAllProgress: "",
    isSaving: false,
    isDownloading: false,
    isDownloadingVoice: false,
    isGeneratingTransitions: false,
    generatingTransitionIndex: null,
    transitionProgress: "",
    isGeneratingVoice: false,
    voiceProgress: "",
    isCombiningReel: false,
    combinedReelUrl: "",
    reelVoiceAudioUrl: this.props.reelVoiceAudioUrl || "",
    reelVoiceTimeline: this.props.reelVoiceTimeline || [],
    imageProvider: "gemini",
    voiceAuthors: [],
    isLoadingVoiceAuthors: false,
    selectedVoiceAuthorId: "",
    selectedVoiceAuthorName: "",
    selectedVoiceAuthorProfilePhotoURL: "",
    globalTheme: "",
    writingStyle: "",
    characterDescription: this.props.characterDescription || "",
    communityTitle: "",
    communityDescription: "",
    communityShortDescription: "",
    communityCategoryName: "",
    communityTags: [],
    buyerPersona: "",
    globalReferenceImages: [],
    videoFormat: "hard-truth",
    useThreeStepValidator: true,
    reelFormatPromptTemplates: getDefaultReelFormatPrompts(),
    threeStepValidatorPromptTemplates: getDefaultThreeStepValidatorPrompts(),
    selectedPromptVideoFormat: "hard-truth",
    isSavingReelPrompts: false,
    reelPromptSaveStatus: "",
    isSavingReelVisualStyle: false,
    reelVisualStyleSaveStatus: "",
    savedReelVisualStyle: "",
    importedScript: null,
    storyCandidates: [],
    selectedStoryCandidateIndex: 0,
    chosenStoryCandidate: null,
    characterMasterReferenceImage: "",
    isGeneratingCharacterRef: false,
    visualStyleReferenceImage: "",
    seedanceApiKey: this.props.seedanceApiKey || process.env.REACT_APP_SEEDANCE_API_KEY || "",
    elevenLabsApiKey: this.props.elevenLabsApiKey || process.env.REACT_APP_ELEVENLABS_API_KEY || "",
    voiceId: this.props.voiceId || "",
    qualityScoreStatus: "",
    isGeneratingImagePrompts: false,
    castCharacters: [],
    isLoadingCast: false,
    selectedCastIds: [],
  };

  // ─── Lifecycle ──────────────────────────────────────────────────────────────

  componentDidMount() {
    if (this.props.existingSegments?.length) {
      const inferredCharacterDescription =
        this.props.characterDescription || this.extractCharacterDescription(this.props.existingSegments);
      this.setState({
        segments: this.props.existingSegments,
        promptInputs: this.props.existingSegments.map((s) => s.imagePrompt),
        scriptInputs: this.props.existingSegments.map((s) => s.script),
        characterDescription: inferredCharacterDescription,
        reelVoiceAudioUrl: this.props.reelVoiceAudioUrl || "",
        reelVoiceTimeline: this.props.reelVoiceTimeline || [],
        generationSubsteps: [],
        currentStep: "complete",
        storyCandidates: [],
        selectedStoryCandidateIndex: 0,
        chosenStoryCandidate: null,
        qualityScores: undefined,
        qualityScoreStatus: "",
      });
    }
    this.setState({
      seedanceApiKey: this.props.seedanceApiKey || process.env.REACT_APP_SEEDANCE_API_KEY || "",
      elevenLabsApiKey: this.props.elevenLabsApiKey || process.env.REACT_APP_ELEVENLABS_API_KEY || "",
      voiceId: this.props.voiceId || "",
      characterDescription: this.props.characterDescription || this.state.characterDescription || "",
    });
    this.loadCommunityStyles();
    this.loadVoiceAuthors();
    this.loadCastCharacters();
  }

  componentDidUpdate(prevProps: ViralReelCreatorProps) {
    if (prevProps.existingSegments !== this.props.existingSegments && this.props.existingSegments?.length) {
      const inferredCharacterDescription =
        this.props.characterDescription || this.extractCharacterDescription(this.props.existingSegments);
      // Only force step change when idle (loading saved data), not during active editing/generation
      const nextStep = this.state.currentStep === "idle" ? "complete" : this.state.currentStep;
      this.setState({
        segments: this.props.existingSegments,
        promptInputs: this.props.existingSegments.map((s) => s.imagePrompt),
        scriptInputs: this.props.existingSegments.map((s) => s.script),
        characterDescription: inferredCharacterDescription,
        reelVoiceAudioUrl: this.props.reelVoiceAudioUrl || "",
        reelVoiceTimeline: this.props.reelVoiceTimeline || [],
        generationSubsteps: [],
        currentStep: nextStep,
        storyCandidates: [],
        selectedStoryCandidateIndex: 0,
        chosenStoryCandidate: null,
        qualityScores: undefined,
        qualityScoreStatus: "",
      });
    }
    if (
      prevProps.communityId !== this.props.communityId ||
      prevProps.stepId !== this.props.stepId
    ) {
      this.loadCommunityStyles();
      this.loadVoiceAuthors();
      this.loadCastCharacters();
    }
    if (prevProps.seedanceApiKey !== this.props.seedanceApiKey && this.props.seedanceApiKey !== undefined) {
      this.setState({ seedanceApiKey: this.props.seedanceApiKey });
    }
    if (prevProps.elevenLabsApiKey !== this.props.elevenLabsApiKey && this.props.elevenLabsApiKey !== undefined) {
      this.setState({ elevenLabsApiKey: this.props.elevenLabsApiKey });
    }
    if (
      prevProps.voiceId !== this.props.voiceId &&
      this.props.voiceId !== undefined &&
      !this.state.selectedVoiceAuthorId
    ) {
      this.setState({ voiceId: this.props.voiceId });
    }
    if (prevProps.reelVoiceAuthorReference !== this.props.reelVoiceAuthorReference) {
      const nextReference = this.props.reelVoiceAuthorReference || null;
      const nextVoiceId = (nextReference?.voiceId || this.props.voiceId || "").trim();
      if (
        (nextReference?.id || "") !== this.state.selectedVoiceAuthorId ||
        nextVoiceId !== (this.state.voiceId || "").trim()
      ) {
        this.setState({
          selectedVoiceAuthorId: nextReference?.id || "",
          selectedVoiceAuthorName: nextReference?.name || "",
          selectedVoiceAuthorProfilePhotoURL: nextReference?.profilePhotoURL || "",
          voiceId: nextVoiceId,
        });
      }
    }
    if (prevProps.characterDescription !== this.props.characterDescription && this.props.characterDescription !== undefined) {
      this.setState({ characterDescription: this.props.characterDescription });
    }
    if (
      prevProps.reelVoiceAudioUrl !== this.props.reelVoiceAudioUrl ||
      prevProps.reelVoiceTimeline !== this.props.reelVoiceTimeline
    ) {
      this.setState({
        reelVoiceAudioUrl: this.props.reelVoiceAudioUrl || "",
        reelVoiceTimeline: this.props.reelVoiceTimeline || [],
      });
    }
  }

  // ─── Data Loading ────────────────────────────────────────────────────────────

  buildVoiceAuthorReferenceFromAuthor = (author: Author | null): ReelVoiceAuthorReference | null => {
    if (!author?.id || !(author.voiceId || "").trim()) return null;
    return {
      id: author.id,
      name: author.fullName || "",
      profilePhotoURL: author.profilePhotoURL || "",
      voiceId: (author.voiceId || "").trim(),
    };
  };

  getSelectedReelVoiceAuthorReference = (): ReelVoiceAuthorReference | null => {
    const {
      selectedVoiceAuthorId,
      selectedVoiceAuthorName,
      selectedVoiceAuthorProfilePhotoURL,
      voiceId,
    } = this.state;

    if (!selectedVoiceAuthorId || !(voiceId || "").trim()) return null;

    return {
      id: selectedVoiceAuthorId,
      name: selectedVoiceAuthorName || "",
      profilePhotoURL: selectedVoiceAuthorProfilePhotoURL || "",
      voiceId: (voiceId || "").trim(),
    };
  };

  loadVoiceAuthors = async () => {
    const { communityId, voiceId: propVoiceId, reelVoiceAuthorReference, setFieldValue } = this.props;
    const fallbackVoiceId = (reelVoiceAuthorReference?.voiceId || propVoiceId || "").trim();

    if (!communityId) {
      this.setState({
        voiceAuthors: [],
        isLoadingVoiceAuthors: false,
        selectedVoiceAuthorId: reelVoiceAuthorReference?.id || "",
        selectedVoiceAuthorName: reelVoiceAuthorReference?.name || "",
        selectedVoiceAuthorProfilePhotoURL: reelVoiceAuthorReference?.profilePhotoURL || "",
        voiceId: fallbackVoiceId,
      });
      return;
    }

    this.setState({ isLoadingVoiceAuthors: true });

    try {
      const authorsSnapshot = await firebase
        .firestore()
        .collection(DB_PATH.AUTHOR)
        .where("communityId", "==", communityId)
        .get();

      const voiceAuthors = authorsSnapshot.docs
        .map((doc) => doc.data() as Author)
        .filter((author) => !!author?.id && !!(author.voiceId || "").trim())
        .sort(
          (a, b) =>
            (a.position ?? Number.MAX_SAFE_INTEGER) -
            (b.position ?? Number.MAX_SAFE_INTEGER)
        );

      const matchedSavedAuthor = reelVoiceAuthorReference
        ? voiceAuthors.find(
            (author) =>
              author.id === reelVoiceAuthorReference.id ||
              (author.voiceId || "").trim() === reelVoiceAuthorReference.voiceId
          ) || null
        : null;
      const matchedFallbackAuthor = fallbackVoiceId
        ? voiceAuthors.find((author) => (author.voiceId || "").trim() === fallbackVoiceId) || null
        : null;
      const initialReference =
        this.buildVoiceAuthorReferenceFromAuthor(
          matchedSavedAuthor || matchedFallbackAuthor || voiceAuthors[0] || null
        ) || reelVoiceAuthorReference || null;

      this.setState({
        voiceAuthors,
        isLoadingVoiceAuthors: false,
        selectedVoiceAuthorId: initialReference?.id || "",
        selectedVoiceAuthorName: initialReference?.name || "",
        selectedVoiceAuthorProfilePhotoURL: initialReference?.profilePhotoURL || "",
        voiceId: (initialReference?.voiceId || fallbackVoiceId || "").trim(),
      });

      if (
        initialReference &&
        (
          !reelVoiceAuthorReference ||
          reelVoiceAuthorReference.id !== initialReference.id ||
          reelVoiceAuthorReference.voiceId !== initialReference.voiceId
        )
      ) {
        setFieldValue?.("reelVoiceAuthorReference", initialReference);
      }
    } catch (error) {
      console.error("Error loading voice authors for reel creator:", error);
      this.setState({
        voiceAuthors: [],
        isLoadingVoiceAuthors: false,
        selectedVoiceAuthorId: reelVoiceAuthorReference?.id || "",
        selectedVoiceAuthorName: reelVoiceAuthorReference?.name || "",
        selectedVoiceAuthorProfilePhotoURL: reelVoiceAuthorReference?.profilePhotoURL || "",
        voiceId: fallbackVoiceId,
      });
    }
  };

  // ─── Cast (multi-character dialogue mode) ───────────────────────────────────

  loadCastCharacters = async () => {
    const { communityId } = this.props;
    if (!communityId) {
      this.setState({ castCharacters: [], selectedCastIds: [], isLoadingCast: false });
      return;
    }
    this.setState({ isLoadingCast: true });
    try {
      const snapshot = await firebase
        .firestore()
        .collection(DB_PATH.COMMUNITY)
        .doc(communityId)
        .collection(DB_PATH.REEL_CHARACTER)
        .orderBy("updatedAt", "desc")
        .get();
      const castCharacters = snapshot.docs
        .map((doc) => ({ ...(doc.data() as ReelCharacter), id: doc.id }))
        .filter((c) => c.status !== "archived");
      this.setState((prev) => ({
        castCharacters,
        isLoadingCast: false,
        // Keep prior selection; recover the cast of a saved dialogue reel.
        selectedCastIds: prev.selectedCastIds.length
          ? prev.selectedCastIds.filter((id) => castCharacters.some((c) => c.id === id))
          : Array.from(
              new Set(
                (prev.segments || [])
                  .flatMap((seg) => seg.characterIds || [])
                  .filter((id) => castCharacters.some((c) => c.id === id))
              )
            ),
      }));
    } catch (error) {
      console.error("Error loading reel characters:", error);
      this.setState({ castCharacters: [], isLoadingCast: false });
    }
  };

  handleToggleCastMember = (characterId: string) => {
    this.setState((prev) => ({
      selectedCastIds: prev.selectedCastIds.includes(characterId)
        ? prev.selectedCastIds.filter((id) => id !== characterId)
        : [...prev.selectedCastIds, characterId],
    }));
  };

  getSelectedCast = (): ReelCastMember[] => {
    const { castCharacters, selectedCastIds } = this.state;
    return selectedCastIds
      .map((id) => castCharacters.find((c) => c.id === id))
      .filter((c): c is ReelCharacter => !!c)
      .map((c) => ({
        characterId: c.id,
        name: c.name,
        visualDescription: c.visualDescription || "",
        personality: c.personality,
        backstory: c.backstory,
        speakingStyle: c.speakingStyle,
        voiceId: c.voiceId,
        referenceImageUrl: c.referenceImageUrl,
      }));
  };

  // Dialogue mode is active when at least two cast members are selected.
  isDialogueMode = (): boolean => this.state.selectedCastIds.length >= 2;

  // Character reference images live as https URLs; Gemini needs data URLs.
  fetchImageAsDataUrl = async (url: string): Promise<string | null> => {
    try {
      const response = await fetch(url);
      if (!response.ok) return null;
      const blob = await response.blob();
      return await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error("Could not fetch character reference image:", error);
      return null;
    }
  };

  getCastReferenceImagesForSegment = async (segment: ReelSegment): Promise<string[]> => {
    const cast = this.getSelectedCast();
    const present = cast.filter(
      (c) => (segment.characterIds || []).includes(c.characterId) && !!c.referenceImageUrl
    );
    const results = await Promise.all(
      present.map((c) => this.fetchImageAsDataUrl(c.referenceImageUrl as string))
    );
    return results.filter((r): r is string => !!r);
  };

  getMergedReelFormatPromptTemplates = (
    storedTemplates?: Record<string, unknown> | null
  ): ReelFormatPromptTemplateMap => {
    const defaults = getDefaultReelFormatPrompts();
    if (!storedTemplates || typeof storedTemplates !== "object") return defaults;
    const merged = { ...defaults };
    Object.entries(storedTemplates).forEach(([format, value]) => {
      if (typeof value === "string" && value.trim()) {
        merged[format] = value;
      }
    });
    return merged;
  };

  getMergedThreeStepValidatorPromptTemplates = (
    storedTemplates?: Record<string, unknown> | null
  ): ThreeStepValidatorPromptTemplateMap => {
    const defaults = getDefaultThreeStepValidatorPrompts();
    if (!storedTemplates || typeof storedTemplates !== "object") return defaults;
    const merged = { ...defaults };
    Object.entries(storedTemplates).forEach(([key, value]) => {
      if (typeof value === "string" && value.trim()) {
        merged[key as keyof ThreeStepValidatorPromptTemplateMap] = value;
      }
    });
    return merged;
  };

  loadCommunityStyles = async () => {
    const { communityId } = this.props;
    if (!communityId) return;
    try {
      const communitySnapshot = await firebase.firestore().collection(DB_PATH.COMMUNITY).doc(communityId).get();
      const data = communitySnapshot.data();
      const reelConfig = await getCommunityReelCreatorConfig(communityId, { communityData: data });
      const reelVisualStyle =
        typeof reelConfig.reelVisualStyle === "string"
          ? reelConfig.reelVisualStyle
          : data?.visualStyle || "";
      // The 3-step validator is always on and uses hard-coded skill-derived prompts.
      // Coach-editable reel format / validator templates are deprecated — we ignore
      // anything saved on the community config and always use defaults.
      this.setState({
        writingStyle: data?.writingStyle || "",
        globalTheme: reelVisualStyle,
        savedReelVisualStyle: reelVisualStyle,
        visualStyleReferenceImage:
          typeof reelConfig.reelVisualStyleReferenceImage === "string"
            ? reelConfig.reelVisualStyleReferenceImage
            : "",
        communityTitle: data?.title || "",
        communityDescription: data?.description || "",
        communityShortDescription: data?.shortDescription || "",
        communityCategoryName: data?.categoryName || "",
        communityTags: Array.isArray(data?.tags) ? data.tags : [],
        buyerPersona: data?.buyerPersona || "",
        useThreeStepValidator: true,
        reelFormatPromptTemplates: getDefaultReelFormatPrompts(),
        threeStepValidatorPromptTemplates: getDefaultThreeStepValidatorPrompts(),
        reelPromptSaveStatus: "",
        reelVisualStyleSaveStatus: "",
      });
      const communityVoiceId = data?.author?.voiceId || data?.voiceId;
      if (communityVoiceId && !this.state.voiceId && !this.state.selectedVoiceAuthorId) {
        this.setState({ voiceId: communityVoiceId });
      }
    } catch (e) {
      console.error("Error loading community styles:", e);
    }
  };

  resizeImageToDataUrl = (blob: Blob, maxSize = 512): Promise<string> =>
    new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(blob);
      img.onload = () => {
        const scale = Math.min(1, maxSize / Math.max(img.width, img.height));
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext("2d")!.drawImage(img, 0, 0, canvas.width, canvas.height);
        URL.revokeObjectURL(url);
        resolve(canvas.toDataURL("image/jpeg", 0.85));
      };
      img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Failed to resize image")); };
      img.src = url;
    });

  handleVoiceAuthorChange = (authorId: string) => {
    const fallbackVoiceId = (this.props.voiceId || "").trim();
    const selectedAuthor =
      this.state.voiceAuthors.find((author) => author.id === authorId) || null;
    const nextReference = this.buildVoiceAuthorReferenceFromAuthor(selectedAuthor);

    this.setState((prev) => ({
      selectedVoiceAuthorId: nextReference?.id || "",
      selectedVoiceAuthorName: nextReference?.name || "",
      selectedVoiceAuthorProfilePhotoURL: nextReference?.profilePhotoURL || "",
      voiceId: (nextReference?.voiceId || fallbackVoiceId || "").trim(),
      reelVoiceAudioUrl: "",
      reelVoiceTimeline: [],
      segments: prev.segments.map((segment) => ({ ...segment, voiceAudioUrl: "" })),
    }));

    this.props.setFieldValue?.("reelVoiceAuthorReference", nextReference);
    this.props.setFieldValue?.("reelVoiceAudioUrl", "");
    this.props.setFieldValue?.("reelVoiceTimeline", []);
  };

  getSyncedNarrationScripts = (): string[] =>
    this.state.segments.map((segment, index) => (this.state.scriptInputs[index] || segment.script || "").trim());

  estimateNarrationDurationMs = (scripts: string[]): number => {
    const totalWords = scripts
      .map((script) => script.split(/\s+/).filter(Boolean).length)
      .reduce((sum, count) => sum + count, 0);
    const pauseCount = Math.max(scripts.filter((script) => script.trim()).length - 1, 0);
    return Math.max(totalWords * 380 + pauseCount * 220, scripts.length * 2200, 1000);
  };

  buildReelVoiceTimeline = (
    scripts: string[],
    totalDurationMs: number
  ): ReelNarrationCue[] => {
    const safeTotalMs = Math.max(Math.round(totalDurationMs), scripts.length);
    const weights = scripts.map((script) => Math.max(script.split(/\s+/).filter(Boolean).length, 1));
    const totalWeight = weights.reduce((sum, weight) => sum + weight, 0) || scripts.length || 1;
    let cursorMs = 0;

    return scripts.map((script, segmentIndex) => {
      const isLast = segmentIndex === scripts.length - 1;
      const weightedDurationMs = isLast
        ? safeTotalMs - cursorMs
        : Math.max(1, Math.round((weights[segmentIndex] / totalWeight) * safeTotalMs));
      const startMs = cursorMs;
      const endMs = isLast ? safeTotalMs : Math.min(safeTotalMs, startMs + weightedDurationMs);
      cursorMs = endMs;
      return {
        segmentIndex,
        startMs,
        endMs: Math.max(endMs, startMs + 1),
        script,
      };
    });
  };

  buildEvenSegmentTimings = (
    segmentCount: number,
    totalDurationMs: number
  ): { start: number; end: number }[] => {
    const safeCount = Math.max(segmentCount, 1);
    const safeTotalMs = Math.max(Math.round(totalDurationMs), safeCount);

    return Array.from({ length: safeCount }, (_, index) => {
      const start = Math.round((index / safeCount) * safeTotalMs);
      const end =
        index === safeCount - 1
          ? safeTotalMs
          : Math.max(start + 1, Math.round(((index + 1) / safeCount) * safeTotalMs));

      return { start, end };
    });
  };

  // ─── AI Image Dispatch ───────────────────────────────────────────────────────

  extractCharacterDescription = (segments: ReelSegment[]): string => {
    const anchor =
      segments
        .map((seg) => extractCharacterAnchor(seg.imagePrompt))
        .find((value): value is string => !!value && value.trim().length > 0) || "";
    return anchor;
  };

  generateAIImage = (prompt: string, perSegmentRefs?: string[]): Promise<string> => {
    const { geminiApiKey } = this.props;
    const { globalReferenceImages, imageProvider, characterMasterReferenceImage, visualStyleReferenceImage } = this.state;
    const hasCharacterRef = !!characterMasterReferenceImage;
    const hasStyleRef = !!visualStyleReferenceImage;
    const promptHasCharacter = /CHARACTER/.test(prompt);

    if (imageProvider === "gpt") {
      if (hasCharacterRef && promptHasCharacter) {
        const cleaned = prompt
          .replace(/CHARACTER_ANCHOR:\s*[^\n]+?(?:\.\s*|\.\s*$)/gi, "")
          .replace(/Shared visual style:\s*[^\n]+?(?:\.\s*|\.\s*$)/gi, "")
          .replace(/\s{2,}/g, " ")
          .trim();
        return generateGptImage(
          cleaned,
          [characterMasterReferenceImage!],
          "The reference image shows the character — match their exact appearance and place them in the scene."
        );
      }
      return generateGptImage(prompt);
    }

    // Gemini path — preserves the prior behavior (all refs always passed, instruction varies by branch).
    const mergedRefs = [
      ...(characterMasterReferenceImage ? [characterMasterReferenceImage] : []),
      ...(visualStyleReferenceImage ? [visualStyleReferenceImage] : []),
      ...(globalReferenceImages || []),
      ...(perSegmentRefs || []),
    ];
    let effectivePrompt = prompt;
    let refInstruction: string | undefined;

    if (hasCharacterRef && promptHasCharacter) {
      effectivePrompt = "The first reference image shows the character — place them in this exact scene: " +
        prompt
          .replace(/CHARACTER_ANCHOR:\s*[^\n]+?(?:\.\s*|\.\s*$)/gi, "")
          .replace(/Shared visual style:\s*[^\n]+?(?:\.\s*|\.\s*$)/gi, "")
          .replace(/\s{2,}/g, " ")
          .trim();
      refInstruction = hasStyleRef
        ? "The first reference image shows the character — match their exact appearance and place them in the scene. The second reference image defines VISUAL STYLE ONLY (lighting, color palette, mood, texture) — do NOT copy or include the person shown in it."
        : undefined;
    } else if (hasStyleRef) {
      refInstruction = "The reference image(s) define VISUAL STYLE ONLY — lighting, color palette, mood, and texture. Do NOT include, copy, or replicate any person shown in the reference images. The scene should contain only what the prompt describes.";
    }

    return generateGeminiImage(geminiApiKey, effectivePrompt, mergedRefs.length ? mergedRefs : undefined, refInstruction);
  };

  // Dialogue mode: pass the reference images of every character present in the
  // shot so Gemini keeps each face consistent across the whole mini movie.
  generateDialogueSegmentImage = async (seg: ReelSegment, prompt: string): Promise<string> => {
    const { geminiApiKey } = this.props;
    const { imageProvider, visualStyleReferenceImage } = this.state;
    const castRefs = await this.getCastReferenceImagesForSegment(seg);
    if (!castRefs.length || imageProvider === "gpt") {
      return this.generateAIImage(prompt, seg.referenceImages);
    }
    const mergedRefs = [
      ...castRefs,
      ...(visualStyleReferenceImage ? [visualStyleReferenceImage] : []),
      ...(seg.referenceImages || []),
    ];
    const refInstruction =
      `The first ${castRefs.length} reference image(s) show the cast characters in this scene. ` +
      `Match their exact appearance (face, hair, wardrobe) and place them in the scene as the prompt describes.` +
      (visualStyleReferenceImage
        ? " Any later reference image defines VISUAL STYLE ONLY (lighting, color palette, mood, texture); do NOT copy or include people from it."
        : "");
    return generateGeminiImage(geminiApiKey, prompt, mergedRefs, refInstruction);
  };

  generateCharacterMasterReference = async () => {
    const { geminiApiKey } = this.props;
    const { characterDescription, globalTheme, communityTitle, communityShortDescription, communityCategoryName, imageProvider } = this.state;

    if (!characterDescription?.trim()) {
      alert("Add a character description first so we know who to generate.");
      return;
    }

    this.setState({ isGeneratingCharacterRef: true });
    try {
      const prompt = buildCharacterReferencePrompt(
        characterDescription,
        globalTheme,
        { title: communityTitle, shortDescription: communityShortDescription, categoryName: communityCategoryName }
      );
      const imageUrl = imageProvider === "gpt"
        ? await generateGptImage(prompt)
        : await generateGeminiImage(geminiApiKey, prompt);
      this.setState({ characterMasterReferenceImage: imageUrl, isGeneratingCharacterRef: false });
    } catch (e) {
      console.error("Error generating character reference:", e);
      alert("Failed to generate character reference. Try again.");
      this.setState({ isGeneratingCharacterRef: false });
    }
  };

  resolveCharacterAnchor = (segments: ReelSegment[], promptInputs: string[]): string => {
    const explicit = (this.state.characterDescription || "").trim();
    if (explicit) return explicit;

    const fromInputs = promptInputs
      .map((prompt) => extractCharacterAnchor(prompt))
      .find((anchor): anchor is string => !!anchor && anchor.trim().length > 0);

    if (fromInputs) return fromInputs;

    const fromSegments = segments
      .map((seg) => extractCharacterAnchor(seg.imagePrompt))
      .find((anchor): anchor is string => !!anchor && anchor.trim().length > 0);

    return fromSegments || "";
  };

  normalizePromptCharacterAnchor = (prompt: string, sharedAnchor?: string): string => {
    const cleanPrompt = (prompt || "").trim();
    const existingAnchor = extractCharacterAnchor(cleanPrompt);
    if (!existingAnchor) return cleanPrompt;
    return applyCharacterAnchor(cleanPrompt, sharedAnchor || existingAnchor);
  };

  handleAddGlobalReference = (file: File | null) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      this.setState((prev) => ({ globalReferenceImages: [...prev.globalReferenceImages, reader.result as string] }));
    };
    reader.readAsDataURL(file);
  };

  handleRemoveGlobalReference = (idx: number) =>
    this.setState((prev) => {
      const refs = [...prev.globalReferenceImages];
      refs.splice(idx, 1);
      return { globalReferenceImages: refs };
    });

  handleVisualStyleReferenceImageChange = (file: File | null) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const MAX = 768;
        const scale = Math.min(1, MAX / Math.max(img.width, img.height));
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext("2d")!.drawImage(img, 0, 0, canvas.width, canvas.height);
        const resized = canvas.toDataURL("image/jpeg", 0.85);
        this.setState({ visualStyleReferenceImage: resized, reelVisualStyleSaveStatus: "" });
      };
      img.src = reader.result as string;
    };
    reader.readAsDataURL(file);
  };

  handleClearVisualStyleReferenceImage = () =>
    this.setState({ visualStyleReferenceImage: "", reelVisualStyleSaveStatus: "" });

  handleCharacterReferenceImageChange = (file: File | null) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const MAX = 768;
        const scale = Math.min(1, MAX / Math.max(img.width, img.height));
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext("2d")!.drawImage(img, 0, 0, canvas.width, canvas.height);
        const resized = canvas.toDataURL("image/jpeg", 0.9);
        this.setState({ characterMasterReferenceImage: resized });
      };
      img.src = reader.result as string;
    };
    reader.readAsDataURL(file);
  };

  handleClearCharacterReferenceImage = () =>
    this.setState({ characterMasterReferenceImage: "" });

  // ─── Prompt Template Handlers ────────────────────────────────────────────────

  handlePromptEditorFormatChange = (format: VideoFormat) =>
    this.setState({ selectedPromptVideoFormat: format, reelPromptSaveStatus: "" });

  persistReelVisualStyle = async () => {
    const { communityId } = this.props;
    const { globalTheme, visualStyleReferenceImage } = this.state;

    if (!communityId) {
      this.setState({ reelVisualStyleSaveStatus: "Save unavailable until this reel belongs to a community." });
      return;
    }

    this.setState({
      isSavingReelVisualStyle: true,
      reelVisualStyleSaveStatus: "Saving reel visual style...",
    });

    try {
      await saveCommunityReelCreatorConfig(communityId, {
        reelVisualStyle: globalTheme || "",
        reelVisualStyleReferenceImage: visualStyleReferenceImage || "",
      });

      this.setState({
        isSavingReelVisualStyle: false,
        reelVisualStyleSaveStatus: "Reel visual style saved.",
        savedReelVisualStyle: globalTheme || "",
      });
    } catch (error) {
      console.error("Error saving reel visual style:", error);
      this.setState({
        isSavingReelVisualStyle: false,
        reelVisualStyleSaveStatus: "Error saving reel visual style. Please try again.",
      });
    }
  };

  handleReelFormatPromptTemplateChange = (format: VideoFormat, value: string) =>
    this.setState((prev) => ({
      reelFormatPromptTemplates: { ...prev.reelFormatPromptTemplates, [format]: value },
    }));

  handleResetReelFormatPromptTemplate = (format: VideoFormat) =>
    this.setState((prev) => ({
      reelFormatPromptTemplates: {
        ...prev.reelFormatPromptTemplates,
        [format]: DEFAULT_REEL_FORMAT_PROMPTS[format] || "",
      },
    }));

  handleThreeStepValidatorPromptTemplateChange = (
    key: keyof ThreeStepValidatorPromptTemplateMap,
    value: string
  ) =>
    this.setState((prev) => ({
      threeStepValidatorPromptTemplates: {
        ...prev.threeStepValidatorPromptTemplates,
        [key]: value,
      },
    }));

  handleResetThreeStepValidatorPromptTemplate = (key: keyof ThreeStepValidatorPromptTemplateMap) =>
    this.setState((prev) => ({
      threeStepValidatorPromptTemplates: {
        ...prev.threeStepValidatorPromptTemplates,
        [key]: DEFAULT_THREE_STEP_VALIDATOR_PROMPTS[key] || "",
      },
    }));

  persistReelPromptConfig = async () => {
    const { communityId } = this.props;
    const {
      reelFormatPromptTemplates,
      useThreeStepValidator,
      threeStepValidatorPromptTemplates,
    } = this.state;
    if (!communityId) return;
    this.setState({ isSavingReelPrompts: true, reelPromptSaveStatus: "Saving prompt templates..." });
    try {
      await saveCommunityReelCreatorConfig(communityId, {
        reelFormatPromptTemplates,
        useThreeStepValidator,
        threeStepValidatorPromptTemplates,
      });
      this.setState({ isSavingReelPrompts: false, reelPromptSaveStatus: "Prompt templates saved for this community." });
    } catch (error) {
      console.error("Error saving reel prompt templates:", error);
      this.setState({ isSavingReelPrompts: false, reelPromptSaveStatus: "Error saving prompt templates. Please try again." });
    }
  };

  // ─── Segment Generation ──────────────────────────────────────────────────────

  getVideoFormatLabel = (format: VideoFormat): string => {
    const labels: Record<VideoFormat, string> = {
      "hard-truth": "Hard Truth",
      storytelling: "Storytelling",
      tutorial: "Tutorial / How-to",
      "before-after": "Before & After",
      "day-in-life": "Day in the Life",
      "ugc-rant": "UGC Rant",
      "historic-arc": "Historic Arc",
      "expert-revelation": "Expert Revelation",
    };

    return labels[format] || format;
  };

  buildGenerationSubsteps = (
    importedScriptCount: number,
    videoFormat: VideoFormat,
    useThreeStepValidator: boolean
  ): GenerationSubstep[] => {
    const validatorSupported = useThreeStepValidator && supportsThreeStepValidator(videoFormat);

    if (importedScriptCount > 0) {
      return [
        { id: "build-segments", label: "Build reel segments", status: "pending" }
      ];
    }

    if (validatorSupported) {
      return [
        { id: "story-selection", label: "Select strongest story", status: "pending" },
        { id: "script-generation", label: "Draft reel script", status: "pending" },
        { id: "scoring", label: "Score script quality", status: "pending" },
        { id: "build-segments", label: "Build reel segments", status: "pending" },
      ];
    }

    return [
      { id: "script-generation", label: "Generate reel script", status: "pending" },
      { id: "build-segments", label: "Build reel segments", status: "pending" },
    ];
  };

  projectGenerationSubsteps = (
    substeps: GenerationSubstep[],
    activeStepId: string,
    activeStatus: GenerationSubstepStatus
  ): GenerationSubstep[] => {
    const activeIndex = substeps.findIndex((step) => step.id === activeStepId);
    if (activeIndex === -1) return substeps;

    return substeps.map((step, index) => {
      if (index < activeIndex) return { ...step, status: "complete" };
      if (index === activeIndex) return { ...step, status: activeStatus };
      return { ...step, status: "pending" };
    });
  };

  syncGenerationProgress = (
    stepId: string,
    progress: string,
    progressPercent: number,
    stepStatus: GenerationSubstepStatus = "active"
  ) => {
    this.setState((prev) => ({
      progress,
      progressPercent,
      generationSubsteps: this.projectGenerationSubsteps(prev.generationSubsteps, stepId, stepStatus),
    }));
  };

  getQualityScoreStatus = (
    importedScriptCount: number,
    scriptCount: number,
    videoFormat: VideoFormat,
    useThreeStepValidator: boolean,
    hasQualityScores: boolean
  ): string => {
    if (hasQualityScores) return "";
    if (!useThreeStepValidator) {
      return "Quality scoring is off because the 3-Step Validator is disabled.";
    }
    if (!supportsThreeStepValidator(videoFormat)) {
      return `Quality scoring is only available for Storytelling and Historic Arc. ${this.getVideoFormatLabel(videoFormat)} uses the direct generation path.`;
    }
    if (importedScriptCount > 0 && scriptCount !== SEGMENT_COUNT) {
      return `Quality scoring expects ${SEGMENT_COUNT} segments. This imported script has ${scriptCount}.`;
    }
    return "Quality scoring did not return structured results for this run.";
  };

  handleSelectStoryCandidate = (index: number) => {
    this.setState({ selectedStoryCandidateIndex: index });
  };

  handleContinueWithSelectedStory = async () => {
    const { text } = this.props;
    const {
      storyCandidates,
      selectedStoryCandidateIndex,
      videoFormat,
      writingStyle,
      reelFormatPromptTemplates,
      threeStepValidatorPromptTemplates,
      useThreeStepValidator,
    } = this.state;

    const chosenStory = storyCandidates[selectedStoryCandidateIndex] || null;
    if (!chosenStory) {
      alert("Select a story candidate first.");
      return;
    }
    if (!text?.trim()) {
      alert("Please add some text content first.");
      return;
    }

    const generationSubsteps = this.state.generationSubsteps.length
      ? this.state.generationSubsteps
      : this.buildGenerationSubsteps(0, videoFormat, useThreeStepValidator);

    this.setState({
      isGenerating: true,
      progress: "Preparing the selected story...",
      progressPercent: 18,
      generationSubsteps: this.projectGenerationSubsteps(generationSubsteps, "script-generation", "active"),
      currentStep: "story-selecting",
      segments: [],
      promptInputs: [],
      scriptInputs: [],
      combinedReelUrl: "",
      reelVoiceAudioUrl: "",
      reelVoiceTimeline: [],
      chosenStoryCandidate: chosenStory,
      qualityScores: undefined,
      qualityScoreStatus: "",
    });

    const onProgress = ({ stepId, message, percent }: ReelGenerationProgressUpdate) => {
      this.syncGenerationProgress(stepId, message, percent);
    };

    try {
      const result = await generateBusinessStoryScriptsFromCandidate(
        text,
        videoFormat,
        chosenStory,
        writingStyle,
        reelFormatPromptTemplates,
        threeStepValidatorPromptTemplates,
        onProgress
      );

      if (!result?.scripts?.length) {
        this.setState({
          isGenerating: false,
          progress: "Could not generate a script for the selected story. Try another option.",
          progressPercent: 0,
          currentStep: "story-selecting",
        });
        return;
      }

      this.syncGenerationProgress(
        "build-segments",
        `Step ${generationSubsteps.length} of ${generationSubsteps.length}: building ${result.scripts.length} segments...`,
        88
      );

      const segments: ReelSegment[] = result.scripts.map((script) => ({
        script,
        imagePrompt: "",
        options: [],
        selectedIndex: 0,
      }));

      const qualityScores = result.scores ?? undefined;
      const qualityScoreStatus = this.getQualityScoreStatus(
        0,
        result.scripts.length,
        videoFormat,
        useThreeStepValidator,
        !!qualityScores
      );

      this.setState({
        segments,
        promptInputs: segments.map((segment) => segment.imagePrompt),
        scriptInputs: segments.map((segment) => segment.script),
        generationSubsteps: this.projectGenerationSubsteps(generationSubsteps, "build-segments", "complete"),
        progress: "Review your segments and generate AI images",
        progressPercent: 100,
        currentStep: "selecting",
        isGenerating: false,
        chosenStoryCandidate: chosenStory,
        qualityScores,
        qualityScoreStatus,
      });
    } catch (error) {
      console.error("Error generating reel segments from selected story:", error);
      this.setState({
        isGenerating: false,
        progress: "Error generating segments for the selected story. Please try again.",
        progressPercent: 0,
        currentStep: "story-selecting",
      });
    }
  };

  generateSegments = async () => {
    const { text } = this.props;
    const {
      writingStyle,
      videoFormat,
      characterDescription,
      communityTitle,
      communityDescription,
      communityShortDescription,
      communityCategoryName,
      communityTags,
      buyerPersona,
      importedScript,
      reelFormatPromptTemplates,
      useThreeStepValidator,
      threeStepValidatorPromptTemplates,
    } = this.state;

    if (!importedScript?.length && !text) { alert("Please add some text content first."); return; }

    const importedScriptCount = importedScript?.length || 0;
    const generationSubsteps = this.buildGenerationSubsteps(importedScriptCount, videoFormat, useThreeStepValidator);
    const initialStep = generationSubsteps[0];

    this.setState({
      isGenerating: true,
      progress: importedScriptCount ? "Preparing your direct script..." : `Preparing ${SEGMENT_COUNT} reel segments...`,
      progressPercent: 6,
      generationSubsteps: initialStep
        ? this.projectGenerationSubsteps(generationSubsteps, initialStep.id, "active")
        : generationSubsteps,
      segments: [],
      promptInputs: [],
      scriptInputs: [],
      combinedReelUrl: "",
      reelVoiceAudioUrl: "",
      reelVoiceTimeline: [],
      currentStep: "generating",
      storyCandidates: [],
      selectedStoryCandidateIndex: 0,
      chosenStoryCandidate: null,
      qualityScores: undefined,
      qualityScoreStatus: "",
    });

    const onProgress = ({ stepId, message, percent }: ReelGenerationProgressUpdate) => {
      this.syncGenerationProgress(stepId, message, percent);
    };

    try {
      // Multi-character dialogue mode: the whole reel is spoken by the selected
      // cast, so skip the narrator pipelines entirely.
      if (!importedScriptCount && this.isDialogueMode()) {
        await this.generateDialogueSegments(generationSubsteps, onProgress);
        return;
      }

      const shouldPauseForStorySelection =
        !importedScriptCount &&
        useThreeStepValidator &&
        supportsThreeStepValidator(videoFormat);

      if (shouldPauseForStorySelection) {
        const storyCandidates = await generateBusinessStoryCandidates(
          text,
          threeStepValidatorPromptTemplates,
          onProgress
        );

        if (!storyCandidates.length) {
          this.setState({
            isGenerating: false,
            progress: "Could not find story options. Please try again.",
            progressPercent: 0,
            currentStep: "idle",
            generationSubsteps: [],
          });
          return;
        }

        this.setState({
          isGenerating: false,
          progress: "Choose the story candidate to continue.",
          progressPercent: 100,
          generationSubsteps: this.projectGenerationSubsteps(generationSubsteps, "story-selection", "complete"),
          currentStep: "story-selecting",
          storyCandidates,
          selectedStoryCandidateIndex: 0,
          chosenStoryCandidate: null,
        });
        return;
      }

      let raw: { script: string; imagePrompt: string }[];
      let reelResult: Awaited<ReturnType<typeof generateReelSegments>> | undefined;
      let qualityScores: Record<string, number> | undefined;
      let resolvedCharacterDescription = characterDescription;

      if (importedScriptCount) {
        raw = importedScript!.map((script) => ({ script, imagePrompt: "" }));
      } else {
        reelResult = await generateReelSegments(
          text,
          videoFormat,
          writingStyle,
          {
            title: communityTitle,
            description: communityDescription,
            shortDescription: communityShortDescription,
            categoryName: communityCategoryName,
            tags: communityTags,
            buyerPersona,
          },
          reelFormatPromptTemplates,
          useThreeStepValidator,
          threeStepValidatorPromptTemplates,
          onProgress
        );
        raw = reelResult.segments;
        qualityScores = reelResult.qualityScores;
      }

      if (!raw.length) {
        this.setState({ progress: "Failed to generate segments. Please try again.", currentStep: "idle", isGenerating: false });
        return;
      }

      if (!importedScriptCount) {
        if (!resolvedCharacterDescription?.trim()) {
          this.setState({ progress: "Identifying story character..." });
          resolvedCharacterDescription = await inferCharacterDescription(raw.map((s) => s.script));
          if (resolvedCharacterDescription) {
            this.setState({ characterDescription: resolvedCharacterDescription });
          }
        }
        if (resolvedCharacterDescription?.trim()) {
          raw = enforceCharacterConsistency(raw, resolvedCharacterDescription);
        }
      }

      this.syncGenerationProgress(
        "build-segments",
        `Step ${generationSubsteps.length} of ${generationSubsteps.length}: building ${raw.length} segments...`,
        88
      );

      const segments: ReelSegment[] = raw.map(({ script, imagePrompt }) => ({
        script,
        imagePrompt,
        options: [],
        selectedIndex: 0,
      }));

      const qualityScoreStatus = this.getQualityScoreStatus(
        importedScriptCount,
        raw.length,
        videoFormat,
        useThreeStepValidator,
        !!qualityScores
      );

      this.setState({
        segments,
        promptInputs: segments.map((s) => s.imagePrompt),
        scriptInputs: segments.map((s) => s.script),
        generationSubsteps: this.projectGenerationSubsteps(generationSubsteps, "build-segments", "complete"),
        progress: "Review your segments and generate AI images",
        progressPercent: 100,
        currentStep: "selecting",
        isGenerating: false,
        qualityScores,
        qualityScoreStatus,
      });
    } catch (e) {
      console.error("Error generating reel segments:", e);
      this.setState({
        progress: "Error generating segments. Please try again.",
        currentStep: "idle",
        isGenerating: false,
        qualityScores: undefined,
        qualityScoreStatus: "",
      });
    }
  };

  generateDialogueSegments = async (
    generationSubsteps: GenerationSubstep[],
    onProgress: (update: ReelGenerationProgressUpdate) => void
  ) => {
    const { text } = this.props;
    const {
      writingStyle,
      videoFormat,
      savedReelVisualStyle,
      communityTitle,
      communityDescription,
      communityShortDescription,
      communityCategoryName,
      communityTags,
      buyerPersona,
    } = this.state;
    const cast = this.getSelectedCast();
    const communityContext = {
      title: communityTitle,
      description: communityDescription,
      shortDescription: communityShortDescription,
      categoryName: communityCategoryName,
      tags: communityTags,
      buyerPersona,
    };

    try {
      const { segments: drafts } = await generateDialogueReelSegments(
        text,
        videoFormat,
        writingStyle,
        cast,
        communityContext,
        onProgress
      );

      const imagePrompts = await generateDialogueImagePrompts(
        drafts,
        cast,
        savedReelVisualStyle,
        communityContext,
        onProgress
      );

      const segments: ReelSegment[] = drafts.map((draft, i) => ({
        script: draft.script,
        imagePrompt: imagePrompts[i] || "",
        options: [],
        selectedIndex: 0,
        speakerCharacterId: draft.speakerCharacterId,
        speakerName: draft.speakerName,
        characterIds: draft.characterIds,
      }));

      this.setState({
        segments,
        promptInputs: segments.map((s) => s.imagePrompt),
        scriptInputs: segments.map((s) => s.script),
        generationSubsteps: this.projectGenerationSubsteps(generationSubsteps, "build-segments", "complete"),
        progress: "Review the dialogue and generate AI images",
        progressPercent: 100,
        currentStep: "selecting",
        isGenerating: false,
        qualityScores: undefined,
        qualityScoreStatus: `Dialogue mode: ${cast.length} characters cast.`,
      });
    } catch (e) {
      console.error("Error generating dialogue segments:", e);
      this.setState({
        progress: "Error generating the dialogue. Please try again.",
        currentStep: "idle",
        isGenerating: false,
        qualityScores: undefined,
        qualityScoreStatus: "",
      });
    }
  };

  // ─── Per-Segment Handlers ────────────────────────────────────────────────────

  handleGenerateAIForSegment = async (idx: number) => {
    const { segments, promptInputs } = this.state;
    const seg = segments[idx];
    const prompt = (promptInputs[idx] || seg?.imagePrompt || "").trim();
    if (!seg || !prompt) return;
    const sharedAnchor = this.resolveCharacterAnchor(segments, promptInputs);
    const consistentPrompt = this.normalizePromptCharacterAnchor(prompt, sharedAnchor);

    this.setState({ aiGeneratingIndex: idx });
    try {
      const imageUrl = seg.speakerCharacterId
        ? await this.generateDialogueSegmentImage(seg, consistentPrompt)
        : await this.generateAIImage(consistentPrompt, seg.referenceImages);
      const option: SlideImageOption = {
        imageUrl,
        thumbnailUrl: imageUrl,
        alt: "AI Generated",
        photographer: this.state.imageProvider === "gpt" ? "GPT Image" : "Gemini",
        isAiGenerated: true,
      };
      this.setState((prev) => {
        const next = [...prev.segments];
        const nextPromptInputs = [...prev.promptInputs];
        if (!next[idx]) return prev;
        next[idx] = { ...next[idx], imagePrompt: consistentPrompt, options: [option, ...next[idx].options], selectedIndex: 0 };
        nextPromptInputs[idx] = consistentPrompt;
        return { ...prev, segments: next, promptInputs: nextPromptInputs };
      });
    } catch (e) {
      console.error("Error generating AI image:", e);
      alert((e as Error)?.message || "Error generating AI image. Try again.");
    } finally {
      this.setState({ aiGeneratingIndex: null });
    }
  };

  handleGenerateAllAIImages = async () => {
    const { segments, promptInputs } = this.state;
    if (!segments.length) return;
    const missingImageIndexes = segments.reduce<number[]>((indexes, segment, index) => {
      if (!segment.options.length) indexes.push(index);
      return indexes;
    }, []);
    if (!missingImageIndexes.length) {
      alert("All segments already have images.");
      return;
    }
    const remainingIndexes = missingImageIndexes.filter((index) => (promptInputs[index] || segments[index]?.imagePrompt || "").trim());
    if (!remainingIndexes.length) {
      alert("Remaining segments are missing image prompts.");
      return;
    }
    const sharedAnchor = this.resolveCharacterAnchor(segments, promptInputs);
    const totalRemaining = remainingIndexes.length;
    let completed = 0;
    const failures: { segment: number; message: string }[] = [];

    this.setState({ aiGeneratingAll: true, aiGeneratingAllProgress: `Generating 0 of ${totalRemaining}...` });

    const provider = this.state.imageProvider;

    await Promise.allSettled(
      remainingIndexes.map(async (i) => {
        const prompt = (promptInputs[i] || segments[i].imagePrompt || "").trim();
        if (!prompt) return;
        const consistentPrompt = this.normalizePromptCharacterAnchor(prompt, sharedAnchor);
        try {
          const imageUrl = segments[i].speakerCharacterId
            ? await this.generateDialogueSegmentImage(segments[i], consistentPrompt)
            : await this.generateAIImage(consistentPrompt, segments[i].referenceImages);
          const option: SlideImageOption = {
            imageUrl,
            thumbnailUrl: imageUrl,
            alt: "AI Generated",
            photographer: provider === "gpt" ? "GPT Image" : "Gemini",
            isAiGenerated: true,
          };
          this.setState((prev) => {
            const next = [...prev.segments];
            const nextPromptInputs = [...prev.promptInputs];
            if (!next[i]) return prev;
            next[i] = { ...next[i], imagePrompt: consistentPrompt, options: [option, ...next[i].options], selectedIndex: 0 };
            nextPromptInputs[i] = consistentPrompt;
            return { ...prev, segments: next, promptInputs: nextPromptInputs };
          });
        } catch (e) {
          const message = (e as Error)?.message || "Unknown error";
          console.error(`Error generating AI image for segment ${i + 1}:`, e);
          failures.push({ segment: i + 1, message });
        } finally {
          completed += 1;
          this.setState({ aiGeneratingAllProgress: `Generating ${completed} of ${totalRemaining}...` });
        }
      })
    );

    this.setState({ aiGeneratingAll: false, aiGeneratingAllProgress: "", aiGeneratingIndex: null });

    if (failures.length) {
      const summary = failures
        .map((f) => `Segment ${f.segment}: ${f.message}`)
        .join("\n\n");
      alert(`${failures.length} of ${totalRemaining} images failed to generate:\n\n${summary}`);
    }
  };

  handleSelectOption = (segIdx: number, optIdx: number) =>
    this.setState((prev) => {
      const next = [...prev.segments];
      if (!next[segIdx]) return prev;
      next[segIdx] = { ...next[segIdx], selectedIndex: optIdx };
      return { ...prev, segments: next };
    });

  handlePromptInputChange = (segIdx: number, value: string) =>
    this.setState((prev) => { const next = [...prev.promptInputs]; next[segIdx] = value; return { ...prev, promptInputs: next }; });

  handleScriptInputChange = (segIdx: number, value: string) =>
    this.setState((prev) => {
      const nextScriptInputs = [...prev.scriptInputs];
      nextScriptInputs[segIdx] = value;
      return {
        ...prev,
        scriptInputs: nextScriptInputs,
        reelVoiceAudioUrl: "",
        reelVoiceTimeline: [],
        segments: prev.segments.map((segment) => ({ ...segment, voiceAudioUrl: "" })),
      };
    });

  handleUploadForSegment = (segIdx: number, file: File | null) => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    const option: SlideImageOption = { imageUrl: url, thumbnailUrl: url, alt: file.name, photographer: "Uploaded", isUploaded: true };
    this.setState((prev) => {
      const next = [...prev.segments];
      if (!next[segIdx]) return prev;
      next[segIdx] = { ...next[segIdx], options: [option, ...next[segIdx].options], selectedIndex: 0 };
      return { ...prev, segments: next };
    });
  };

  handleAddReferenceImage = (segIdx: number, file: File | null) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      this.setState((prev) => {
        const next = [...prev.segments];
        if (!next[segIdx]) return prev;
        next[segIdx] = { ...next[segIdx], referenceImages: [...(next[segIdx].referenceImages || []), reader.result as string] };
        return { ...prev, segments: next };
      });
    };
    reader.readAsDataURL(file);
  };

  handleRemoveReferenceImage = (segIdx: number, refIdx: number) =>
    this.setState((prev) => {
      const next = [...prev.segments];
      if (!next[segIdx]) return prev;
      const refs = [...(next[segIdx].referenceImages || [])];
      refs.splice(refIdx, 1);
      next[segIdx] = { ...next[segIdx], referenceImages: refs };
      return { ...prev, segments: next };
    });

  // ─── Kling Transitions & Voice ──────────────────────────────────────────────

  uploadBlobToStorage = async (blob: Blob, path: string, contentType?: string): Promise<string> => {
    const ref = firebase.storage().ref(path);
    await ref.put(blob, contentType ? { contentType } : undefined);
    return ref.getDownloadURL();
  };

  getAudioDuration = (url: string, timeoutMs: number = 15000): Promise<number> =>
    new Promise((resolve, reject) => {
      const audio = new Audio();
      let settled = false;

      const cleanup = () => {
        audio.onloadedmetadata = null;
        audio.onerror = null;
        window.clearTimeout(timeoutId);
      };

      const finishResolve = (duration: number) => {
        if (settled) return;
        settled = true;
        cleanup();
        resolve(duration);
      };

      const finishReject = (error: Error) => {
        if (settled) return;
        settled = true;
        cleanup();
        reject(error);
      };

      const timeoutId = window.setTimeout(() => {
        finishReject(new Error("Timed out while reading audio metadata"));
      }, timeoutMs);

      audio.preload = "metadata";
      audio.onloadedmetadata = () => {
        if (!Number.isFinite(audio.duration) || audio.duration <= 0) {
          finishReject(new Error("Audio metadata did not contain a valid duration"));
          return;
        }
        finishResolve(audio.duration);
      };
      audio.onerror = () => finishReject(new Error("Failed to load audio metadata"));
      audio.src = url;
      audio.load();
    });

  getAccurateAudioDurationMs = async (url: string, fallbackMs: number): Promise<number> => {
    if (!url) return fallbackMs;

    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch audio: ${response.status}`);
      }

      const audioBlob = await response.blob();
      const AudioContextCtor =
        window.AudioContext || ((window as any).webkitAudioContext as typeof AudioContext | undefined);

      if (AudioContextCtor) {
        const audioContext = new AudioContextCtor();
        try {
          const audioBuffer = await new Promise<AudioBuffer>((resolve, reject) => {
            const arrayBufferPromise = audioBlob.arrayBuffer();
            arrayBufferPromise
              .then((arrayBuffer) => {
                const copiedBuffer = arrayBuffer.slice(0);
                const maybePromise = audioContext.decodeAudioData(
                  copiedBuffer,
                  (decoded) => resolve(decoded),
                  (error) => reject(error)
                );

                if (maybePromise && typeof (maybePromise as Promise<AudioBuffer>).then === "function") {
                  (maybePromise as Promise<AudioBuffer>).then(resolve).catch(reject);
                }
              })
              .catch(reject);
          });

          const decodedDurationMs = Math.round(audioBuffer.duration * 1000);
          if (Number.isFinite(decodedDurationMs) && decodedDurationMs > 0) {
            return decodedDurationMs;
          }
        } finally {
          await audioContext.close().catch(() => undefined);
        }
      }

      const objectUrl = URL.createObjectURL(audioBlob);
      try {
        const metadataDurationMs = await this.getAudioDuration(objectUrl).then((duration) =>
          Math.round(duration * 1000)
        );
        if (Number.isFinite(metadataDurationMs) && metadataDurationMs > 0) {
          return metadataDurationMs;
        }
      } finally {
        URL.revokeObjectURL(objectUrl);
      }
    } catch (error) {
      console.error("Could not determine accurate audio duration:", error);
    }

    return fallbackMs;
  };

  wait = (ms: number): Promise<void> =>
    new Promise((resolve) => setTimeout(resolve, ms));

  withRetryCacheBust = (url: string, attempt: number): string => {
    if (attempt <= 1) return url;
    if (url.startsWith("blob:") || url.startsWith("data:")) return url;
    const separator = url.includes("?") ? "&" : "?";
    return `${url}${separator}retryAttempt=${attempt}&t=${Date.now()}`;
  };

  withMediaVersion = (url: string, version: number = Date.now()): string => {
    if (!url || url.startsWith("blob:") || url.startsWith("data:")) return url;

    const normalizedUrl = url.replace(/([?&])mediaVersion=\d+(&?)/, (_match, prefix, suffix) => {
      if (prefix === "?" && suffix) return "?";
      return suffix ? prefix : "";
    });
    const separator = normalizedUrl.includes("?") ? "&" : "?";
    return `${normalizedUrl}${separator}mediaVersion=${version}`;
  };

  loadImageWithRetries = async (
    url: string,
    label: string,
    maxAttempts: number = 3
  ): Promise<HTMLImageElement> => {
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const image = await new Promise<HTMLImageElement>((resolve, reject) => {
          const img = new Image();
          img.crossOrigin = "anonymous";

          const timeoutId = window.setTimeout(() => {
            reject(new Error(`${label} timed out while loading.`));
          }, 12000);

          img.onload = () => {
            window.clearTimeout(timeoutId);
            if (!img.naturalWidth || !img.naturalHeight) {
              reject(new Error(`${label} loaded but has invalid dimensions.`));
              return;
            }
            resolve(img);
          };

          img.onerror = () => {
            window.clearTimeout(timeoutId);
            reject(new Error(`${label} failed to load.`));
          };

          img.src = this.withRetryCacheBust(url, attempt);
        });

        return image;
      } catch (error) {
        if (attempt < maxAttempts) {
          await this.wait(250 * attempt);
        }
      }
    }

    throw new Error(`${label} failed after ${maxAttempts} attempts. Please try another image.`);
  };

  drawSegmentCaption = (
    ctx: CanvasRenderingContext2D,
    caption: string,
    segmentProgress: number,
    canvasWidth: number,
    canvasHeight: number
  ) => {
    const text = (caption || "").trim();
    if (!text) return;

    const words = text.split(/\s+/).filter(Boolean);
    if (!words.length) return;

    const easedProgress =
      segmentProgress < 0.5
        ? 2 * segmentProgress * segmentProgress
        : 1 - Math.pow(-2 * segmentProgress + 2, 2) / 2;
    const visibleWordCount = Math.max(
      1,
      Math.min(words.length, Math.ceil(easedProgress * words.length))
    );
    const visibleText = words.slice(0, visibleWordCount).join(" ");

    const fontSize = 62;
    const lineHeight = fontSize * 1.28;
    const maxWidth = canvasWidth * 0.8;
    const centerY = canvasHeight * 0.79;

    ctx.save();
    ctx.font = `700 ${fontSize}px "Helvetica Neue", Helvetica, Arial, sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    const lines: string[] = [];
    let currentLine = "";

    for (const word of visibleText.split(/\s+/)) {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      if (ctx.measureText(testLine).width > maxWidth && currentLine) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        currentLine = testLine;
      }
    }

    if (currentLine) lines.push(currentLine);
    const displayedLines = lines.slice(-2);

    const totalHeight = displayedLines.length * lineHeight;
    const startY = centerY - totalHeight / 2 + lineHeight / 2;
    const horizontalPadding = 28;
    const verticalPadding = 18;

    for (let i = 0; i < displayedLines.length; i++) {
      const y = startY + i * lineHeight;
      const line = displayedLines[i];
      const textWidth = ctx.measureText(line).width;
      const boxWidth = textWidth + horizontalPadding * 2;
      const boxHeight = fontSize + verticalPadding * 2;
      const boxX = canvasWidth / 2 - boxWidth / 2;
      const boxY = y - boxHeight / 2;

      ctx.save();
      ctx.fillStyle = "rgba(0, 0, 0, 0.78)";
      ctx.beginPath();
      ctx.roundRect(boxX, boxY, boxWidth, boxHeight, 18);
      ctx.fill();

      ctx.strokeStyle = "rgba(255, 255, 255, 0.18)";
      ctx.lineWidth = 2;
      ctx.stroke();

      ctx.fillStyle = "var(--color-surface)";
      ctx.shadowColor = "rgba(0, 0, 0, 0.35)";
      ctx.shadowBlur = 2;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 1;
      ctx.fillStyle = "var(--color-surface)";
      ctx.fillText(line, canvasWidth / 2, y);
      ctx.restore();
    }

    ctx.restore();
  };

  loadScript = (url: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[src="${url}"]`);
      if (existing) {
        resolve();
        return;
      }
      const script = document.createElement("script");
      script.src = url;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`Failed to load ${url}`));
      document.head.appendChild(script);
    });
  };

  loadFFmpeg = async (
    loadingProgress: string
  ): Promise<{ ffmpeg: any; fetchFile: (data: Blob | File | string | ArrayBuffer | Uint8Array) => Promise<Uint8Array> }> => {
    await this.loadScript(FFMPEG_SCRIPT_URL);
    const ffmpegLib = (window as any).FFmpeg;
    if (!ffmpegLib?.createFFmpeg || !ffmpegLib?.fetchFile) {
      throw new Error("FFmpeg library failed to load");
    }

    const { createFFmpeg, fetchFile } = ffmpegLib;
    const ffmpeg = createFFmpeg({
      log: false,
      corePath: FFMPEG_CORE_URL,
      mainName: "main",
    });

    if (!ffmpeg.isLoaded()) {
      this.setState({ progress: loadingProgress });
      await ffmpeg.load();
    }

    return { ffmpeg, fetchFile };
  };

  getCinematicTransitionPrompt = (_segIdx: number, isEndingClip: boolean = false): string =>
    isEndingClip
      ? "Natural cinematic motion on the current scene. Animate from the starting frame only with no ending frame. Keep the same character identity and visual style. No text in frame."
      : "Natural cinematic transition between scenes. Choose the most fitting camera movement based on the content. No text in frame.";

  withTimeout = async <T,>(promise: Promise<T>, timeoutMs: number, message: string): Promise<T> => {
    let timeoutId: number | undefined;
    const timeout = new Promise<never>((_, reject) => {
      timeoutId = window.setTimeout(() => reject(new Error(message)), timeoutMs);
    });

    try {
      return await Promise.race([promise, timeout]);
    } finally {
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
    }
  };

  persistTransitionSegments = async (
    segments: ReelSegment[],
    communityId?: string,
    stepId?: string
  ): Promise<ReelSegment[]> => {
    if (!communityId || !stepId) {
      return segments;
    }

    this.setState({ progress: "Preparing transition data for save..." });
    const persistedSegments = await this.withTimeout(
      this.prepareSegmentsForPersistence(segments, communityId, stepId),
      TRANSITION_AUTOSAVE_TIMEOUT_MS,
      "Preparing transition data timed out. The transition was generated, but autosave did not finish."
    );

    this.setState({ progress: "Saving transition data..." });
    await this.withTimeout(
      firebase
        .firestore()
        .collection(DB_PATH.PLAN_STEP)
        .doc(stepId)
        .set({ reelSegments: persistedSegments }, { merge: true }),
      TRANSITION_AUTOSAVE_TIMEOUT_MS,
      "Saving transition data timed out. The transition was generated, but autosave did not finish."
    );

    return persistedSegments;
  };

  handleGenerateKlingTransitions = async () => {
    const { communityId, stepId, setFieldValue } = this.props;
    const { segments, seedanceApiKey } = this.state;
    const key = (seedanceApiKey || "").trim();

    if (!key) {
      alert("Seedance API key is required.");
      return;
    }
    if (!segments.length) {
      alert("Generate segments first.");
      return;
    }

    this.setState({
      isGeneratingTransitions: true,
      transitionProgress: "Generating Seedance transitions...",
      progress: "Generating Seedance transitions...",
    });

    const next = [...segments];
    const pending = segments.filter((s) => !s.transitionVideoUrl);
    if (!pending.length) {
      alert("All transition clips are already generated.");
      this.setState({ isGeneratingTransitions: false });
      return;
    }

    try {
      let completed = 0;
      const total = pending.length;
      await Promise.allSettled(
        segments.map(async (seg, i) => {
          if (next[i].transitionVideoUrl) return;
          const start = seg?.options?.[seg.selectedIndex]?.imageUrl;
          const end = segments[i + 1]?.options?.[segments[i + 1].selectedIndex]?.imageUrl;
          if (!start) return;
          const isEndingClip = i === segments.length - 1;
          try {
            const remoteVideoUrl = await generateSeedanceTransitionVideo(key, start, end || undefined, {
              durationSeconds: 5,
              prompt: this.getCinematicTransitionPrompt(i, isEndingClip),
            });
            next[i] = { ...next[i], transitionVideoUrl: remoteVideoUrl };
            completed++;
            this.setState({
              segments: [...next],
              combinedReelUrl: "",
              transitionProgress: `${completed} / ${total} transition clips generated...`,
              progress: `${completed} / ${total} transition clips generated...`,
            });
          } catch (err) {
            console.error(`Seedance transition ${i + 1} failed:`, err);
            completed++;
            this.setState({
              transitionProgress: `${completed} / ${total} done (clip ${i + 1} failed)...`,
            });
          }
        })
      );

      let persistedSegments = next;
      let autosaveFailed = false;
      setFieldValue?.("reelSegments", next);
      if (communityId && stepId) {
        try {
          persistedSegments = await this.persistTransitionSegments(next, communityId, stepId);
          setFieldValue?.("reelSegments", persistedSegments);
        } catch (saveErr) {
          console.error("Could not persist transitions to Firestore:", saveErr);
          const message = saveErr instanceof Error ? saveErr.message : "Autosave failed.";
          autosaveFailed = true;
          this.setState({
            transitionProgress: `Transitions generated, but autosave failed: ${message}`,
            progress: "Transitions generated. Autosave failed; try Save Reel.",
          });
        }
      }
      if (autosaveFailed) {
        this.setState({ segments: persistedSegments, combinedReelUrl: "" });
      } else {
        this.setState({
          segments: persistedSegments,
          combinedReelUrl: "",
          transitionProgress: "All missing transition clips generated.",
          progress: "All missing transition clips generated.",
        });
      }
    } catch (error) {
      console.error("Error generating Seedance transitions:", error);
      this.setState({
        transitionProgress: `Seedance transition failed: ${(error as Error).message}`,
        progress: "Seedance transition generation failed.",
      });
    } finally {
      this.setState({ isGeneratingTransitions: false });
    }
  };

  handleGenerateTransitionForSegment = async (segIdx: number) => {
    const { communityId, stepId, setFieldValue } = this.props;
    const { segments, seedanceApiKey } = this.state;
    const key = (seedanceApiKey || "").trim();

    if (!key) { alert("Seedance API key is required."); return; }

    const start = segments[segIdx]?.options?.[segments[segIdx].selectedIndex]?.imageUrl;
    const endSeg = segments[segIdx + 1];
    const end = endSeg?.options?.[endSeg.selectedIndex]?.imageUrl;
    if (!start) { alert("Generate an image for this segment first."); return; }
    const isEndingClip = segIdx === segments.length - 1;
    const clipLabel = isEndingClip ? `ending clip ${segIdx + 1}` : `transition ${segIdx + 1} → ${segIdx + 2}`;

    this.setState({ isGeneratingTransitions: true, generatingTransitionIndex: segIdx, transitionProgress: `Generating ${clipLabel}...` });

    try {
      const remoteVideoUrl = await generateSeedanceTransitionVideo(key, start, end || undefined, {
        durationSeconds: 5,
        prompt: this.getCinematicTransitionPrompt(segIdx, isEndingClip),
      });

      const next = segments.map((s, i) => i === segIdx ? { ...s, transitionVideoUrl: remoteVideoUrl } : s);
      this.setState({ segments: next, combinedReelUrl: "", transitionProgress: `${isEndingClip ? `Ending clip ${segIdx + 1}` : `Transition ${segIdx + 1} → ${segIdx + 2}`} done.` });
      setFieldValue?.("reelSegments", next);
      if (communityId && stepId) {
        try {
          const persistedSegments = await this.persistTransitionSegments(next, communityId, stepId);
          this.setState({ segments: persistedSegments });
          setFieldValue?.("reelSegments", persistedSegments);
          this.setState({ progress: "Transition saved.", transitionProgress: `${clipLabel} saved.` });
        } catch (saveErr) {
          console.error("Could not persist transition URL to Firestore:", saveErr);
          const message = saveErr instanceof Error ? saveErr.message : "Autosave failed.";
          this.setState({
            progress: "Transition generated. Autosave failed; try Save Reel.",
            transitionProgress: `${clipLabel} generated, but autosave failed: ${message}`,
          });
        }
      }
    } catch (error) {
      console.error("Error generating Seedance transition:", error);
      this.setState({ transitionProgress: `Transition ${segIdx + 1} → ${segIdx + 2} failed: ${(error as Error).message}` });
    } finally {
      this.setState({ isGeneratingTransitions: false, generatingTransitionIndex: null });
    }
  };

  handleGenerateVoiceTracks = async () => {
    const { communityId, stepId, setFieldValue } = this.props;
    const { segments, elevenLabsApiKey, voiceId } = this.state;
    const key = (elevenLabsApiKey || "").trim();
    const voice = (voiceId || "").trim();
    const reelVoiceAuthorReference = this.getSelectedReelVoiceAuthorReference();
    const scripts = this.getSyncedNarrationScripts();

    // Dialogue reels render each line in the speaking character's own voice.
    if (segments.length && segments.every((s) => !!s.speakerCharacterId)) {
      await this.generateDialogueVoiceTracks();
      return;
    }

    if (!key) {
      alert("ElevenLabs API key is required.");
      return;
    }
    if (!voice) {
      alert("Voice ID is required to generate reel narration.");
      return;
    }
    if (!segments.length) {
      alert("Generate segments first.");
      return;
    }
    if (!scripts.some((script) => script)) {
      alert("Add reel scripts before generating narration.");
      return;
    }

    this.setState({
      isGeneratingVoice: true,
      voiceProgress: "Generating reel narration...",
      progress: "Generating reel narration...",
    });

    const next = [...segments];
    try {
      this.setState({
        voiceProgress: "Requesting narration from ElevenLabs...",
        progress: "Requesting narration from ElevenLabs...",
      });

      const narrationText = prepareNarrationText(scripts);
      const audioBlob = await generateElevenLabsVoice(key, voice, narrationText);
      const previewUrl = URL.createObjectURL(audioBlob);
      this.setState({
        voiceProgress: "Processing reel narration...",
        progress: "Processing reel narration...",
      });
      const narrationDurationMs = await this.getAudioDuration(previewUrl)
        .then((duration) => Math.max(duration * 1000, this.estimateNarrationDurationMs(scripts)))
        .catch((error) => {
          console.error("Could not read generated reel narration duration:", error);
          return this.estimateNarrationDurationMs(scripts);
        });
      let audioUrl = previewUrl;

      if (communityId && stepId) {
        this.setState({
          voiceProgress: "Uploading reel narration...",
          progress: "Uploading reel narration...",
        });
        audioUrl = await this.uploadBlobToStorage(
          audioBlob,
          `${DB_PATH.AUDIO}/${communityId}/${stepId}/reel-narration/full.mp3`,
          "audio/mpeg"
        );
        audioUrl = this.withMediaVersion(audioUrl);
        URL.revokeObjectURL(previewUrl);
      }

      const reelVoiceTimeline = this.buildReelVoiceTimeline(scripts, narrationDurationMs);

      for (let i = 0; i < next.length; i++) {
        next[i] = { ...next[i], script: scripts[i] || next[i].script, voiceAudioUrl: "" };
      }

      let persistedSegments = next;
      if (communityId && stepId) {
        this.setState({
          voiceProgress: "Preparing reel assets for save...",
          progress: "Preparing reel assets for save...",
        });
        persistedSegments = await this.prepareSegmentsForPersistence(next, communityId, stepId);
      }

      this.setState({
        segments: persistedSegments,
        combinedReelUrl: "",
        reelVoiceAudioUrl: audioUrl,
        reelVoiceTimeline,
        voiceProgress: "Reel narration generated.",
        progress: "Reel narration generated.",
      });
      if (communityId && stepId) {
        await firebase
          .firestore()
          .collection(DB_PATH.PLAN_STEP)
          .doc(stepId)
          .set(
            {
              reelSegments: persistedSegments,
              reelVoiceAuthorReference,
              reelVoiceAudioUrl: audioUrl,
              reelVoiceTimeline,
            },
            { merge: true }
          );
      }
      setFieldValue?.("reelSegments", persistedSegments);
      setFieldValue?.("reelVoiceAuthorReference", reelVoiceAuthorReference);
      setFieldValue?.("reelVoiceAudioUrl", audioUrl);
      setFieldValue?.("reelVoiceTimeline", reelVoiceTimeline);
    } catch (error) {
      console.error("Error generating reel narration:", error);
      this.setState({
        voiceProgress: `Voice generation failed: ${(error as Error).message}`,
        progress: "Voice generation failed.",
      });
    } finally {
      this.setState({ isGeneratingVoice: false });
    }
  };

  // Per-character dialogue audio: one ElevenLabs call per segment with the
  // speaking character's own voice. Per-segment voiceAudioUrl drives segment
  // timings downstream, so no full-track narration or timeline is needed.
  generateDialogueVoiceTracks = async () => {
    const { communityId, stepId, setFieldValue } = this.props;
    const { segments, elevenLabsApiKey, castCharacters, voiceId } = this.state;
    const key = (elevenLabsApiKey || "").trim();
    const fallbackVoice = (voiceId || "").trim();
    const scripts = this.getSyncedNarrationScripts();

    if (!key) {
      alert("ElevenLabs API key is required.");
      return;
    }
    if (!segments.length) {
      alert("Generate segments first.");
      return;
    }

    const voiceFor = (seg: ReelSegment): { voiceId: string; character?: ReelCharacter } => {
      const character = castCharacters.find((c) => c.id === seg.speakerCharacterId);
      return {
        voiceId: (character?.voiceId || fallbackVoice || "").trim(),
        character,
      };
    };

    const missingVoice = segments
      .map((seg, i) => ({ seg, i }))
      .filter(({ seg }) => !voiceFor(seg).voiceId);
    if (missingVoice.length) {
      const names = Array.from(
        new Set(missingVoice.map(({ seg }) => seg.speakerName || "Unknown"))
      ).join(", ");
      alert(
        `These characters have no voice yet: ${names}. Assign an ElevenLabs voice in the Characters tab, or pick a reel voice as fallback.`
      );
      return;
    }
    const missingScript = segments.findIndex((seg, i) => !(scripts[i] || seg.script || "").trim());
    if (missingScript !== -1) {
      alert(`Segment ${missingScript + 1} has no line. Every dialogue segment needs one.`);
      return;
    }

    this.setState({
      isGeneratingVoice: true,
      voiceProgress: "Generating character voices...",
      progress: "Generating character voices...",
    });

    const next = [...segments];
    try {
      for (let i = 0; i < next.length; i++) {
        const { voiceId: segVoice, character } = voiceFor(next[i]);
        const line = (scripts[i] || next[i].script || "").trim();
        this.setState({
          voiceProgress: `Voicing segment ${i + 1}/${next.length} (${next[i].speakerName || "speaker"})...`,
          progress: `Voicing segment ${i + 1}/${next.length}...`,
        });
        const blob = await generateElevenLabsVoice(
          key,
          segVoice,
          prepareNarrationText([line]),
          character?.voiceSettings
        );
        let audioUrl = URL.createObjectURL(blob);
        if (communityId && stepId) {
          const uploaded = await this.uploadBlobToStorage(
            blob,
            `${DB_PATH.AUDIO}/${communityId}/${stepId}/reel-dialogue/segment-${i}.mp3`,
            "audio/mpeg"
          );
          URL.revokeObjectURL(audioUrl);
          audioUrl = this.withMediaVersion(uploaded);
        }
        next[i] = { ...next[i], script: line, voiceAudioUrl: audioUrl };
      }

      let persistedSegments = next;
      if (communityId && stepId) {
        this.setState({
          voiceProgress: "Preparing reel assets for save...",
          progress: "Preparing reel assets for save...",
        });
        persistedSegments = await this.prepareSegmentsForPersistence(next, communityId, stepId);
      }

      this.setState({
        segments: persistedSegments,
        combinedReelUrl: "",
        reelVoiceAudioUrl: "",
        reelVoiceTimeline: [],
        voiceProgress: "Character voices generated.",
        progress: "Character voices generated.",
      });
      if (communityId && stepId) {
        await firebase
          .firestore()
          .collection(DB_PATH.PLAN_STEP)
          .doc(stepId)
          .set(
            {
              reelSegments: persistedSegments,
              reelVoiceAudioUrl: "",
              reelVoiceTimeline: [],
            },
            { merge: true }
          );
      }
      setFieldValue?.("reelSegments", persistedSegments);
      setFieldValue?.("reelVoiceAudioUrl", "");
      setFieldValue?.("reelVoiceTimeline", []);
    } catch (error) {
      console.error("Error generating character voices:", error);
      this.setState({
        voiceProgress: `Voice generation failed: ${(error as Error).message}`,
        progress: "Voice generation failed.",
      });
    } finally {
      this.setState({ isGeneratingVoice: false });
    }
  };

  handleRewriteDimension = async (segmentIndex: number, dimension: string) => {
    const {
      scriptInputs,
      segments,
      videoFormat,
      useThreeStepValidator,
      chosenStoryCandidate,
      threeStepValidatorPromptTemplates,
    } = this.state;

    const focusMap: Record<string, string> = {
      hook: "Make this segment hit harder. Open with 'One day in [year], [name]...' — immediate tension, real name, real year.",
      pain: "Make this segment more painful and concrete. Add a number that stings.",
      move: "Make the decisive move sharper. One sentence that drops the move without explanation.",
      reframe: "Flip the story harder. One short concrete punch — no explanation, no soft insight.",
      lesson: "Make this segment more blunt and directed at the viewer. Cut any generic advice.",
    };

    const focus = focusMap[dimension];
    if (!focus) return;

    const allScripts = scriptInputs.length ? scriptInputs : segments.map((s) => s.script);
    const targetedFocus = `${focus} The full script is provided for context only. Rewrite ONLY segment ${segmentIndex + 1}. Return ONLY a JSON array of exactly 1 string — the rewritten segment. Do not return the full script.`;

    this.setState({ progress: `Rewriting ${dimension}...`, isGenerating: true });

    const rewritten = await rewriteBusinessStoryScripts(
      allScripts,
      chosenStoryCandidate,
      threeStepValidatorPromptTemplates,
      targetedFocus
    );

    if (rewritten?.length) {
      const updatedScripts = [...allScripts];
      if (rewritten[0]) updatedScripts[segmentIndex] = rewritten[0];

      let refreshedQualityScores = this.state.qualityScores;
      let qualityScoreStatus = this.state.qualityScoreStatus;

      if (useThreeStepValidator && supportsThreeStepValidator(videoFormat) && updatedScripts.length === SEGMENT_COUNT) {
        this.setState({ progress: "Refreshing quality scores...", progressPercent: 42 });
        refreshedQualityScores = (await scoreBusinessStoryScripts(
          updatedScripts,
          threeStepValidatorPromptTemplates
        )) || undefined;
        qualityScoreStatus = this.getQualityScoreStatus(
          0,
          updatedScripts.length,
          videoFormat,
          useThreeStepValidator,
          !!refreshedQualityScores
        );
      }

      this.setState((prev) => ({
        scriptInputs: updatedScripts,
        segments: prev.segments.map((seg, i) => ({ ...seg, script: updatedScripts[i] ?? seg.script })),
        isGenerating: false,
        progress: "",
        progressPercent: 0,
        qualityScores: refreshedQualityScores,
        qualityScoreStatus,
      }));
    } else {
      this.setState({ isGenerating: false, progress: "", progressPercent: 0 });
    }
  };

  handleGenerateImagePrompts = async () => {
    const { segments, scriptInputs, characterDescription, globalTheme, communityTitle, communityDescription, communityShortDescription, communityCategoryName, communityTags, buyerPersona } = this.state;
    const scripts = scriptInputs.length ? scriptInputs : segments.map((s) => s.script);
    this.setState({ isGeneratingImagePrompts: true });
    try {
      const imagePrompts = await generateImagePromptsForImportedScripts(
        scripts,
        characterDescription,
        globalTheme,
        { title: communityTitle, description: communityDescription, shortDescription: communityShortDescription, categoryName: communityCategoryName, tags: communityTags, buyerPersona }
      );
      const updatedSegments = segments.map((seg, i) => ({
        ...seg,
        imagePrompt: imagePrompts[i] || `Cinematic 9:16 portrait scene representing: ${seg.script}. Show a single clear visual moment, realistic lighting, no text in image.`,
      }));
      this.setState({
        segments: updatedSegments,
        promptInputs: updatedSegments.map((s) => s.imagePrompt),
        isGeneratingImagePrompts: false,
      });
    } catch (e) {
      console.error("Error generating image prompts:", e);
      this.setState({ isGeneratingImagePrompts: false });
    }
  };

  handleImportImagePrompts = (rawPrompts: string) => {
    const text = rawPrompts.trim();
    if (!text) return;
    const hasParagraphs = /\n\s*\n/.test(text);
    const blocks = hasParagraphs
      ? text.split(/\n\s*\n+/).map((b) => b.trim()).filter(Boolean)
      : text.split("\n").map((b) => b.trim()).filter(Boolean);
    if (!blocks.length) return;
    this.setState((prev) => {
      const updatedSegments = prev.segments.map((seg, i) =>
        i < blocks.length ? { ...seg, imagePrompt: blocks[i] } : seg
      );
      return {
        segments: updatedSegments,
        promptInputs: updatedSegments.map((s) => s.imagePrompt),
      };
    });
  };

  handleClear = () =>
    this.setState({
      segments: [],
      progress: "",
      progressPercent: 0,
      generationSubsteps: [],
      currentStep: "idle",
      promptInputs: [],
      scriptInputs: [],
      aiGeneratingIndex: null,
      aiGeneratingAll: false,
      aiGeneratingAllProgress: "",
      combinedReelUrl: "",
      isDownloading: false,
      isDownloadingVoice: false,
      isGeneratingTransitions: false,
      transitionProgress: "",
      isGeneratingVoice: false,
      voiceProgress: "",
      reelVoiceAudioUrl: "",
      reelVoiceTimeline: [],
      globalReferenceImages: [],
      storyCandidates: [],
      selectedStoryCandidateIndex: 0,
      chosenStoryCandidate: null,
      qualityScores: undefined,
      qualityScoreStatus: "",
    });

  // ─── Save ────────────────────────────────────────────────────────────────────

  uploadBase64ToStorage = async (base64: string, path: string): Promise<string> => {
    const blob = await (await fetch(base64)).blob();
    const timeout = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error("Upload timed out after 60s")), 60000)
    );
    const uploaded = await Promise.race([
      uploadMedia({
        body: blob,
        kind: "image",
        firebasePath: path,
      }),
      timeout,
    ]);
    return uploaded.url;
  };

  prepareSegmentsForPersistence = async (
    segments: ReelSegment[],
    communityId?: string,
    stepId?: string
  ): Promise<ReelSegment[]> => {
    const processed: ReelSegment[] = [];

    for (let i = 0; i < segments.length; i++) {
      const seg = { ...segments[i] };
      const processedOptions: SlideImageOption[] = [];

      for (let j = 0; j < seg.options.length; j++) {
        const opt = { ...seg.options[j] };
        if (opt.imageUrl.startsWith("data:") && communityId && stepId) {
          try {
            const url = await this.uploadBase64ToStorage(
              opt.imageUrl,
              `${DB_PATH.VIDEO}/${communityId}/${stepId}/reel/segment-${i}-opt-${j}.png`
            );
            opt.imageUrl = url;
            opt.thumbnailUrl = url;
          } catch (error) {
            console.error(`Failed to upload segment ${i + 1} option ${j + 1}:`, error);
          }
        }
        processedOptions.push(opt);
      }

      const { referenceImages, ...segWithoutRefs } = seg;
      processed.push({ ...segWithoutRefs, options: processedOptions });
    }

    return processed;
  };

  saveSegments = async () => {
    const { communityId, stepId, setFieldValue } = this.props;
    const {
      segments,
      scriptInputs,
      promptInputs,
      writingStyle,
      globalTheme,
      buyerPersona,
      useThreeStepValidator,
      reelFormatPromptTemplates,
      threeStepValidatorPromptTemplates,
      reelVoiceAudioUrl,
      reelVoiceTimeline,
    } = this.state;
    const reelVoiceAuthorReference = this.getSelectedReelVoiceAuthorReference();
    if (!segments.length) { alert("No segments to save."); return; }

    this.setState({ isSaving: true, progress: "Preparing segments for save..." });
    try {
      // Sync latest edits from inputs back into segments
      const synced = segments.map((seg, i) => ({
        ...seg,
        script: scriptInputs[i] || seg.script,
        imagePrompt: promptInputs[i] || seg.imagePrompt,
      }));

      this.setState({ progress: "Preparing media assets for save..." });
      const processed = await this.prepareSegmentsForPersistence(synced, communityId, stepId);

      let processedReelVoiceAudioUrl = reelVoiceAudioUrl || "";
      if (
        processedReelVoiceAudioUrl &&
        processedReelVoiceAudioUrl.startsWith("blob:") &&
        communityId &&
        stepId
      ) {
        this.setState({ progress: "Uploading reel narration..." });
        const audioBlob = await (await fetch(processedReelVoiceAudioUrl)).blob();
        processedReelVoiceAudioUrl = await this.uploadBlobToStorage(
          audioBlob,
          `${DB_PATH.AUDIO}/${communityId}/${stepId}/reel-narration/full.mp3`,
          audioBlob.type || "audio/mpeg"
        );
        processedReelVoiceAudioUrl = this.withMediaVersion(processedReelVoiceAudioUrl);
      }

      this.setState({ progress: "Saving to database..." });
      if (communityId && stepId) {
        const db = firebase.firestore();
        await db
          .collection(DB_PATH.PLAN_STEP)
          .doc(stepId)
          .set(
            {
              reelSegments: processed,
              reelVoiceAuthorReference,
              reelVoiceAudioUrl: processedReelVoiceAudioUrl,
              reelVoiceTimeline,
            },
            { merge: true }
          );
        await db.collection(DB_PATH.COMMUNITY).doc(communityId).set(
          {
            writingStyle: writingStyle || "",
            buyerPersona: buyerPersona || "",
          },
          { merge: true }
        );
        await saveCommunityReelCreatorConfig(communityId, {
          reelVisualStyle: globalTheme || "",
          reelVisualStyleReferenceImage: this.state.visualStyleReferenceImage || "",
          useThreeStepValidator,
          reelFormatPromptTemplates,
          threeStepValidatorPromptTemplates,
        });
      }

      this.setState({
        segments: processed,
        reelVoiceAudioUrl: processedReelVoiceAudioUrl,
        savedReelVisualStyle: globalTheme || "",
      });
      setFieldValue?.("reelSegments", processed);
      setFieldValue?.("reelVoiceAuthorReference", reelVoiceAuthorReference);
      setFieldValue?.("reelVoiceAudioUrl", processedReelVoiceAudioUrl);
      setFieldValue?.("reelVoiceTimeline", reelVoiceTimeline);
      this.setState({ progress: "Reel saved successfully!", currentStep: "complete", isSaving: false });
    } catch (e) {
      console.error("Error saving reel segments:", e);
      this.setState({ progress: "Error saving. Please try again.", isSaving: false });
    }
  };

  // ─── Download ─────────────────────────────────────────────────────────────────

  triggerBrowserDownload = async (url: string, filename?: string) => {
    if (!url) return;
    let downloadUrl = url;
    let shouldRevokeObjectUrl = false;

    if (!url.startsWith("blob:") && !url.startsWith("data:")) {
      try {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`Download request failed with status ${response.status}`);
        }
        const blob = await response.blob();
        downloadUrl = URL.createObjectURL(blob);
        shouldRevokeObjectUrl = true;
      } catch (error) {
        console.error("Falling back to direct browser download:", error);
        downloadUrl = url;
      }
    }

    const anchor = document.createElement("a");
    anchor.href = downloadUrl;
    if (filename) anchor.download = filename;
    anchor.rel = "noopener noreferrer";
    anchor.style.display = "none";
    document.body.appendChild(anchor);
    anchor.click();
    window.setTimeout(() => {
      if (anchor.parentNode) anchor.parentNode.removeChild(anchor);
      if (shouldRevokeObjectUrl) URL.revokeObjectURL(downloadUrl);
    }, 0);
  };

  downloadAllImages = async () => {
    const { segments } = this.state;
    const withImages = segments.filter((s) => s.options.length > 0);
    if (!withImages.length) { alert("No images to download."); return; }

    this.setState({ progress: "Downloading images...", isDownloading: true });
    try {
      for (let i = 0; i < segments.length; i++) {
        const seg = segments[i];
        const selected = seg.options[seg.selectedIndex];
        if (!selected) continue;

        await new Promise((r) => setTimeout(r, 200));
        const link = document.createElement("a");
        link.download = `reel-segment-${String(i + 1).padStart(2, "0")}.png`;
        link.href = selected.imageUrl;
        link.click();
      }
      this.setState({ progress: `Downloaded ${withImages.length} images!` });
    } finally {
      this.setState({ isDownloading: false });
    }
  };

  downloadCombinedVoiceTracks = async () => {
    const { segments, reelVoiceAudioUrl } = this.state;
    if (reelVoiceAudioUrl) {
      await this.triggerBrowserDownload(reelVoiceAudioUrl, `reel-narration-${Date.now()}.mp3`);
      return;
    }

    const voiceUrls = segments.map((s) => s.voiceAudioUrl).filter((v): v is string => !!v);
    if (!voiceUrls.length) {
      alert("No generated voice tracks found.");
      return;
    }
    if (voiceUrls.length !== segments.length) {
      alert("Only some legacy segment voice tracks exist. Regenerate reel narration to get the full reel audio.");
      return;
    }

    if (voiceUrls.length === 1) {
      await this.triggerBrowserDownload(voiceUrls[0], `reel-voice-${Date.now()}.mp3`);
      return;
    }

    this.setState({ isDownloadingVoice: true, progress: "Combining voice tracks..." });
    try {
      const { ffmpeg, fetchFile } = await this.loadFFmpeg("Loading audio processor...");

      const inputFiles: string[] = [];
      for (let i = 0; i < voiceUrls.length; i++) {
        this.setState({ progress: `Loading voice track ${i + 1}/${voiceUrls.length}...` });
        const response = await fetch(voiceUrls[i]);
        const blob = await response.blob();
        const fileName = `voice-${i}.mp3`;
        ffmpeg.FS("writeFile", fileName, await fetchFile(blob));
        inputFiles.push(fileName);
      }

      const concatList = inputFiles.map((f) => `file '${f}'`).join("\n");
      ffmpeg.FS("writeFile", "concat.txt", concatList);

      this.setState({ progress: "Combining voice into one track..." });
      await ffmpeg.run(
        "-f", "concat",
        "-safe", "0",
        "-i", "concat.txt",
        "-c:a", "libmp3lame",
        "-b:a", "192k",
        "-y",
        "combined-voice.mp3"
      );

      const mp3Data = ffmpeg.FS("readFile", "combined-voice.mp3");
      const mp3Blob = new Blob([mp3Data.buffer], { type: "audio/mpeg" });
      const mp3Url = URL.createObjectURL(mp3Blob);

      inputFiles.forEach((f) => ffmpeg.FS("unlink", f));
      ffmpeg.FS("unlink", "concat.txt");
      ffmpeg.FS("unlink", "combined-voice.mp3");

      await this.triggerBrowserDownload(mp3Url, `reel-combined-voice-${Date.now()}.mp3`);

      setTimeout(() => URL.revokeObjectURL(mp3Url), 1000);
      this.setState({ progress: "Combined voice downloaded." });
    } catch (error) {
      console.error("Error combining voice tracks:", error);
      this.setState({ progress: "Could not combine voice tracks. Please try again." });
      alert("Could not combine voice tracks.");
    } finally {
      this.setState({ isDownloadingVoice: false });
    }
  };

  getPreferredRecorderMimeType = (): string => {
    const candidates = [
      "video/webm;codecs=vp9,opus",
      "video/webm;codecs=vp8,opus",
      "video/webm",
    ];

    return (
      candidates.find(
        (candidate) =>
          typeof MediaRecorder !== "undefined" &&
          typeof MediaRecorder.isTypeSupported === "function" &&
          MediaRecorder.isTypeSupported(candidate)
      ) || "video/webm"
    );
  };

  renderAnimatedImageReel = async (): Promise<string> => {
    const { segments, reelVoiceAudioUrl } = this.state;

    const selectedSegments = segments.map((segment, index) => {
      const selected = segment.options[segment.selectedIndex];
      return {
        index,
        imageUrl: selected?.imageUrl || "",
        voiceAudioUrl: segment.voiceAudioUrl || "",
      };
    });

    const missingImage = selectedSegments.find((segment) => !segment.imageUrl);
    if (missingImage) {
      throw new Error(`Segment ${missingImage.index + 1} is missing a selected image.`);
    }

    const legacyVoiceCount = selectedSegments.filter((segment) => !!segment.voiceAudioUrl).length;
    if (!reelVoiceAudioUrl && legacyVoiceCount > 0 && legacyVoiceCount !== selectedSegments.length) {
      throw new Error("Only some legacy segment voice tracks are present. Generate reel narration again to cover the full reel.");
    }

    const narrationFallbackMs = selectedSegments.length * DEFAULT_ANIMATED_SEGMENT_SECONDS * 1000;
    // Prefer the duration stored in the voice timeline (computed accurately at generation time)
    // over re-detecting from the URL, which can be wrong for VBR MP3 files.
    const timelineLastCue = this.state.reelVoiceTimeline?.length
      ? this.state.reelVoiceTimeline[this.state.reelVoiceTimeline.length - 1]
      : null;
    const timelineDurationMs = timelineLastCue?.endMs ?? 0;
    const segmentTimings = reelVoiceAudioUrl
      ? this.buildEvenSegmentTimings(
          selectedSegments.length,
          timelineDurationMs > 0
            ? timelineDurationMs
            : await this.getAccurateAudioDurationMs(reelVoiceAudioUrl, narrationFallbackMs)
        )
      : (
          await Promise.all(
            selectedSegments.map(async (segment) => {
              if (!segment.voiceAudioUrl) return DEFAULT_ANIMATED_SEGMENT_SECONDS;
              try {
                const duration = await this.getAudioDuration(segment.voiceAudioUrl);
                return Number.isFinite(duration) && duration > 0
                  ? Math.max(duration, MIN_ANIMATED_SEGMENT_SECONDS)
                  : DEFAULT_ANIMATED_SEGMENT_SECONDS;
              } catch (error) {
                console.error("Could not read voice duration for animated reel:", error);
                return DEFAULT_ANIMATED_SEGMENT_SECONDS;
              }
            })
          )
        ).reduce<{ start: number; end: number }[]>(
          (timings, duration) => {
            const start = timings.length ? timings[timings.length - 1].end : 0;
            timings.push({ start, end: start + duration * 1000 });
            return timings;
          },
          []
        );
    const totalDurationMs = segmentTimings.length ? segmentTimings[segmentTimings.length - 1].end : 0;
    if (!totalDurationMs) {
      throw new Error("No reel duration available.");
    }

    const canvas = document.createElement("canvas");
    canvas.width = 1080;
    canvas.height = 1920;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Could not initialize canvas renderer.");

    this.setState({ progress: "Loading animated reel assets..." });

    const images = await Promise.all(
      selectedSegments.map((segment) =>
        this.loadImageWithRetries(segment.imageUrl, `Segment ${segment.index + 1} image`, 3)
      )
    );

    const audioUrls = reelVoiceAudioUrl
      ? [reelVoiceAudioUrl]
      : selectedSegments
          .map((segment) => segment.voiceAudioUrl)
          .filter((url): url is string => !!url);
    const shouldCaptureLiveAudio = !reelVoiceAudioUrl && audioUrls.length > 0;

    let audioContext: AudioContext | null = null;
    let audioDestination: MediaStreamAudioDestinationNode | null = null;
    const audioElements: HTMLAudioElement[] = [];

    if (shouldCaptureLiveAudio) {
      audioContext = new AudioContext();
      await audioContext.resume();
      audioDestination = audioContext.createMediaStreamDestination();

      for (const url of audioUrls) {
        const audio = new Audio();
        audio.crossOrigin = "anonymous";
        audio.src = url;
        audio.preload = "auto";

        await new Promise<void>((resolve) => {
          audio.oncanplaythrough = () => resolve();
          audio.onerror = () => resolve();
          audio.load();
        });

        try {
          const source = audioContext.createMediaElementSource(audio);
          const gainNode = audioContext.createGain();
          gainNode.gain.value = 1;
          source.connect(gainNode);
          gainNode.connect(audioDestination);
          gainNode.connect(audioContext.destination);
        } catch (error) {
          console.error("Could not connect animated reel audio source:", error);
        }

        audioElements.push(audio);
      }
    }

    const canvasStream = canvas.captureStream(REEL_FRAME_RATE);
    const audioTrack = audioDestination?.stream.getAudioTracks?.()[0];
    if (audioTrack) {
      canvasStream.addTrack(audioTrack);
    }

    const mediaRecorder = new MediaRecorder(canvasStream, {
      mimeType: this.getPreferredRecorderMimeType(),
      videoBitsPerSecond: 5000000,
    });
    const chunks: Blob[] = [];
    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) chunks.push(event.data);
    };

    mediaRecorder.start(100);

    if (audioElements.length > 0) {
      audioElements[0].play().catch(console.error);
      for (let i = 0; i < audioElements.length - 1; i++) {
        audioElements[i].onended = () => {
          audioElements[i + 1]?.play().catch(console.error);
        };
      }
    }

    const startTime = performance.now();
    // Render 1 extra second beyond the audio so the canvas video is always longer
    // than the narration — FFmpeg will trim to exact audio length via -t.
    const renderDurationMs = reelVoiceAudioUrl ? totalDurationMs + 1000 : totalDurationMs;
    this.setState({ progress: "Rendering animated image reel..." });

    await new Promise<void>((resolve) => {
      const renderFrame = () => {
        const wallElapsed = performance.now() - startTime;
        const clampedElapsed = Math.min(Math.max(wallElapsed, 0), totalDurationMs - 1);
        const foundSegmentIndex = segmentTimings.findIndex(
          (timing) => clampedElapsed >= timing.start && clampedElapsed < timing.end
        );
        const currentSegmentIndex =
          foundSegmentIndex >= 0
            ? foundSegmentIndex
            : Math.max(segmentTimings.length - 1, 0);
        const timing = segmentTimings[currentSegmentIndex];
        const currentImage = images[currentSegmentIndex];

        const segmentSpan = Math.max(timing.end - timing.start, 1);
        const rawProgress = Math.min(
          Math.max((clampedElapsed - timing.start) / segmentSpan, 0),
          1
        );
        const ease =
          rawProgress < 0.5
            ? 2 * rawProgress * rawProgress
            : 1 - Math.pow(-2 * rawProgress + 2, 2) / 2;

        ctx.fillStyle = "var(--color-text)";
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        let extraScale = 1;
        let panX = 0;
        let panY = 0;
        let rotation = 0;
        const pan = 50;

        switch (Math.max(currentSegmentIndex, 0) % 8) {
          case 0:
            extraScale = 1 + ease * 0.18;
            panX = -ease * pan * 0.6;
            panY = -ease * pan;
            break;
          case 1:
            extraScale = 1.2 - ease * 0.12;
            panX = ease * pan * 0.4;
            panY = ease * pan * 0.3;
            break;
          case 2:
            extraScale = 1.1;
            panX = -pan + ease * pan * 2;
            break;
          case 3:
            extraScale = 1 + ease * 0.15;
            rotation = ease * 1.5 * (Math.PI / 180);
            break;
          case 4:
            extraScale = 1 + ease * 0.2;
            panX = ease * pan * 0.7;
            panY = ease * pan * 0.8;
            break;
          case 5:
            extraScale = 1.18 - ease * 0.1;
            rotation = -ease * 1.2 * (Math.PI / 180);
            panY = -ease * pan * 0.3;
            break;
          case 6:
            extraScale = 1.05 + ease * 0.08;
            panX = pan - ease * pan * 2;
            panY = -ease * pan * 0.2;
            break;
          default:
            extraScale = 1.12;
            panX = -pan * 0.8 + ease * pan * 1.6;
            panY = pan * 0.6 - ease * pan * 1.2;
            break;
        }

        const srcWidth = currentImage.naturalWidth || canvas.width;
        const srcHeight = currentImage.naturalHeight || canvas.height;
        const scale = Math.max(canvas.width / srcWidth, canvas.height / srcHeight) * extraScale;
        const width = srcWidth * scale;
        const height = srcHeight * scale;
        const cx = canvas.width / 2 + panX;
        const cy = canvas.height / 2 + panY;

        ctx.save();
        if (rotation !== 0) {
          ctx.translate(cx, cy);
          ctx.rotate(rotation);
          ctx.drawImage(currentImage, -width / 2, -height / 2, width, height);
        } else {
          ctx.drawImage(currentImage, cx - width / 2, cy - height / 2, width, height);
        }
        ctx.restore();

        // ── Hook caption overlay (first 3 seconds, TikTok style) ───────────────
        const HOOK_DURATION_MS = 3000;
        const HOOK_FADE_MS = 350;
        if (wallElapsed < HOOK_DURATION_MS) {
          const hookText = (this.state.scriptInputs[0] || segments[0]?.script || "").trim();
          if (hookText) {
            let hookOpacity = 1;
            if (wallElapsed < HOOK_FADE_MS) hookOpacity = wallElapsed / HOOK_FADE_MS;
            else if (wallElapsed > HOOK_DURATION_MS - HOOK_FADE_MS) hookOpacity = (HOOK_DURATION_MS - wallElapsed) / HOOK_FADE_MS;

            const FONT_SIZE = 82;
            const MAX_LINE_WIDTH = canvas.width - 160;

            ctx.save();
            ctx.globalAlpha = Math.max(0, Math.min(1, hookOpacity));
            ctx.font = `bold ${FONT_SIZE}px Arial, sans-serif`;
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";

            const words = hookText.split(" ");
            const lines: string[] = [];
            let currentLine = "";
            for (const word of words) {
              const testLine = currentLine ? `${currentLine} ${word}` : word;
              if (ctx.measureText(testLine).width > MAX_LINE_WIDTH && currentLine) {
                lines.push(currentLine);
                currentLine = word;
              } else {
                currentLine = testLine;
              }
            }
            if (currentLine) lines.push(currentLine);

            const lineHeight = FONT_SIZE * 1.3;
            const totalTextHeight = lines.length * lineHeight;
            const captionStartY = canvas.height * 0.30 - totalTextHeight / 2;

            for (let li = 0; li < lines.length; li++) {
              const lineY = captionStartY + li * lineHeight;
              ctx.strokeStyle = "var(--color-text)";
              ctx.lineWidth = 14;
              ctx.lineJoin = "round";
              ctx.strokeText(lines[li], canvas.width / 2, lineY);
              ctx.fillStyle = "var(--color-surface)";
              ctx.fillText(lines[li], canvas.width / 2, lineY);
            }
            ctx.restore();
          }
        }

        if (wallElapsed < renderDurationMs) {
          requestAnimationFrame(renderFrame);
        } else {
          mediaRecorder.stop();
          resolve();
        }
      };

      renderFrame();
    });

    await new Promise<void>((resolve) => {
      mediaRecorder.onstop = () => resolve();
    });

    for (const audio of audioElements) {
      audio.pause();
      audio.src = "";
      audio.load();
    }
    await audioContext?.close();

    return URL.createObjectURL(new Blob(chunks, { type: "video/webm" }));
  };

  convertWebMToMp4 = async (
    webmUrl: string,
    narrationAudioUrl?: string,
    narrationDurationMs?: number
  ): Promise<{ url: string; blob: Blob }> => {
    this.setState({ progress: "Converting animated reel to MP4..." });
    const { ffmpeg, fetchFile } = await this.loadFFmpeg("Loading video encoder...");

    const response = await fetch(webmUrl);
    const webmBlob = await response.blob();

    ffmpeg.FS("writeFile", "input.webm", await fetchFile(webmBlob));
    let narrationFileName = "";

    if (narrationAudioUrl) {
      this.setState({ progress: "Muxing full reel narration into MP4..." });
      const narrationResponse = await fetch(narrationAudioUrl);
      const narrationBlob = await narrationResponse.blob();
      narrationFileName = "reel-narration.mp3";
      ffmpeg.FS("writeFile", narrationFileName, await fetchFile(narrationBlob));
      // Use the known audio duration (-t) so the output is never clipped short.
      // The canvas video is rendered slightly longer than the audio, so -t trims
      // the video to match the audio exactly.
      const durationArgs: string[] = narrationDurationMs && narrationDurationMs > 0
        ? ["-t", String(narrationDurationMs / 1000)]
        : [];
      await ffmpeg.run(
        "-r",
        String(REEL_FRAME_RATE),
        "-i",
        "input.webm",
        "-i",
        narrationFileName,
        "-map",
        "0:v:0",
        "-map",
        "1:a:0",
        "-c:v",
        "libx264",
        "-preset",
        "ultrafast",
        "-crf",
        "28",
        "-pix_fmt",
        "yuv420p",
        "-profile:v",
        "baseline",
        "-r",
        String(REEL_FRAME_RATE),
        "-vsync",
        "cfr",
        "-c:a",
        "aac",
        "-b:a",
        "128k",
        "-ac",
        "2",
        "-ar",
        "44100",
        ...durationArgs,
        "-movflags",
        "+faststart",
        "-y",
        "output.mp4"
      );
    } else {
      await ffmpeg.run(
        "-r",
        String(REEL_FRAME_RATE),
        "-i",
        "input.webm",
        "-c:v",
        "libx264",
        "-preset",
        "ultrafast",
        "-crf",
        "28",
        "-pix_fmt",
        "yuv420p",
        "-profile:v",
        "baseline",
        "-r",
        String(REEL_FRAME_RATE),
        "-vsync",
        "cfr",
        "-c:a",
        "aac",
        "-b:a",
        "128k",
        "-ac",
        "2",
        "-ar",
        "44100",
        "-movflags",
        "+faststart",
        "-y",
        "output.mp4"
      );
    }

    const mp4Data = ffmpeg.FS("readFile", "output.mp4");
    const mp4Blob = new Blob([mp4Data.buffer], { type: "video/mp4" });
    const mp4Url = URL.createObjectURL(mp4Blob);

    ffmpeg.FS("unlink", "input.webm");
    if (narrationFileName) {
      ffmpeg.FS("unlink", narrationFileName);
    }
    ffmpeg.FS("unlink", "output.mp4");

    return { url: mp4Url, blob: mp4Blob };
  };

  downloadAnimatedImageReel = async () => {
    const { combinedReelUrl } = this.state;

    if (combinedReelUrl.startsWith("blob:")) {
      URL.revokeObjectURL(combinedReelUrl);
    }

    this.setState({
      isCombiningReel: true,
      progress: "Seedance API key not configured. Rendering animated image reel...",
    });

    try {
      const webmUrl = await this.renderAnimatedImageReel();
      try {
        const timelineLastCue = this.state.reelVoiceTimeline?.length
          ? this.state.reelVoiceTimeline[this.state.reelVoiceTimeline.length - 1]
          : null;
        const narrationDurationMs = timelineLastCue?.endMs ?? 0;
        const mp4 = await this.convertWebMToMp4(
          webmUrl,
          this.state.reelVoiceAudioUrl || undefined,
          narrationDurationMs || undefined
        );
        URL.revokeObjectURL(webmUrl);

        this.setState({
          combinedReelUrl: mp4.url,
          progress: "Animated reel ready! Downloading MP4...",
        });

        await this.triggerBrowserDownload(mp4.url, `reel-animated-${Date.now()}.mp4`);

        this.setState({ progress: "Animated reel MP4 downloaded." });
      } catch (error) {
        URL.revokeObjectURL(webmUrl);
        throw new Error(`Could not convert animated reel to MP4: ${(error as Error).message}`);
      }
    } catch (error) {
      console.error("Error rendering animated image reel:", error);
      this.setState({
        progress: `Failed to render animated reel: ${(error as Error).message}`,
      });
      alert((error as Error).message || "Could not render animated reel.");
    } finally {
      this.setState({ isCombiningReel: false });
    }
  };

  // ─── Combine Reel ────────────────────────────────────────────────────────────

  downloadCombinedReel = async () => {
    const { communityId, stepId } = this.props;
    const { segments, seedanceApiKey, combinedReelUrl } = this.state;
    const hasSeedanceKey = !!(seedanceApiKey || "").trim();

    if (combinedReelUrl) {
      await this.triggerBrowserDownload(
        combinedReelUrl,
        `${hasSeedanceKey ? "reel-combined" : "reel-animated"}-${Date.now()}.mp4`
      );
      this.setState({
        progress: hasSeedanceKey ? "Combined reel downloaded." : "Animated reel MP4 downloaded.",
      });
      return;
    }

    const videoUrls = segments
      .map((s) => s.transitionVideoUrl)
      .filter((url): url is string => !!url);

    if (!videoUrls.length) {
      await this.downloadAnimatedImageReel();
      return;
    }
    if (!communityId || !stepId) {
      alert("Community ID and Step ID are required to combine the reel.");
      return;
    }

    this.setState({ isCombiningReel: true, progress: "Combining reel via Cloud Function..." });
    try {
      const combineReelFn = firebase.functions().httpsCallable("combineReelTransitions", { timeout: 540000 });
      const result = await combineReelFn({ videoUrls, stepId, communityId, narrationAudioUrl: this.state.reelVoiceAudioUrl || undefined });
      const { success, downloadUrl, error } = result.data as { success?: boolean; downloadUrl?: string; error?: string };

      if (!success || !downloadUrl) {
        throw new Error(error || "Combined reel finished without a download URL.");
      }

      this.setState({ combinedReelUrl: downloadUrl, progress: "Reel combined! Downloading..." });

      await this.triggerBrowserDownload(downloadUrl, `reel-combined-${Date.now()}.mp4`);

      this.setState({ progress: "Combined reel downloaded." });
    } catch (error) {
      console.error("Error combining reel:", error);
      this.setState({ progress: `Failed to combine reel: ${(error as Error).message}` });
      alert("Could not combine reel. Please try again.");
    } finally {
      this.setState({ isCombiningReel: false });
    }
  };

  // ─── Render ──────────────────────────────────────────────────────────────────

  render() {
    const { text } = this.props;
    const {
      isGenerating, isSaving, progress, progressPercent, generationSubsteps, segments, currentStep,
      promptInputs, scriptInputs, imageProvider, videoFormat,
      storyCandidates, selectedStoryCandidateIndex,
      aiGeneratingAll, aiGeneratingAllProgress, aiGeneratingIndex, isDownloading,
      isDownloadingVoice, isCombiningReel, combinedReelUrl,
      reelVoiceAudioUrl, voiceAuthors, selectedVoiceAuthorId,
      isGeneratingTransitions, generatingTransitionIndex, transitionProgress, isGeneratingVoice, voiceProgress,
    } = this.state;

    const hasContent = !!text?.trim();

    return (
        <ViralReelStudioView
          segments={segments}
          promptInputs={promptInputs}
          scriptInputs={scriptInputs}
          currentStep={currentStep}
          progress={progress}
          progressPercent={progressPercent}
          generationSubsteps={generationSubsteps}
          isGenerating={isGenerating}
          isSaving={isSaving}
          isDownloading={isDownloading}
          isDownloadingVoice={isDownloadingVoice}
          isCombiningReel={isCombiningReel}
          combinedReelUrl={combinedReelUrl}
          aiGeneratingIndex={aiGeneratingIndex}
          aiGeneratingAll={aiGeneratingAll}
          aiGeneratingAllProgress={aiGeneratingAllProgress}
          isGeneratingVoice={isGeneratingVoice}
          voiceProgress={voiceProgress}
          isGeneratingTransitions={isGeneratingTransitions}
          generatingTransitionIndex={generatingTransitionIndex}
          transitionProgress={transitionProgress}
          reelVoiceAudioUrl={reelVoiceAudioUrl}
          storyCandidates={storyCandidates}
          selectedStoryCandidateIndex={selectedStoryCandidateIndex}
          videoFormat={videoFormat}
          imageProvider={imageProvider}
          voiceAuthors={voiceAuthors}
          selectedVoiceAuthorId={selectedVoiceAuthorId}
          castCharacters={this.state.castCharacters}
          selectedCastIds={this.state.selectedCastIds}
          onToggleCastMember={this.handleToggleCastMember}
          hasContent={hasContent}
          canSave={!!this.props.communityId && !!this.props.stepId}
          primaryColor={this.props.primaryColor}
          onFormatChange={(f) =>
            this.setState({
              videoFormat: f,
              selectedPromptVideoFormat: f,
              reelPromptSaveStatus: "",
            })
          }
          onProviderChange={(p) => this.setState({ imageProvider: p })}
          onVoiceAuthorChange={this.handleVoiceAuthorChange}
          onGenerate={this.generateSegments}
          onSelectStoryCandidate={this.handleSelectStoryCandidate}
          onContinueWithSelectedStory={this.handleContinueWithSelectedStory}
          onRefreshStories={this.generateSegments}
          onScriptChange={this.handleScriptInputChange}
          onPromptChange={this.handlePromptInputChange}
          onSelectOption={this.handleSelectOption}
          onGenerateAI={this.handleGenerateAIForSegment}
          onGenerateAll={this.handleGenerateAllAIImages}
          onUpload={this.handleUploadForSegment}
          onGenerateTransition={this.handleGenerateTransitionForSegment}
          onGenerateTransitions={this.handleGenerateKlingTransitions}
          onGenerateVoice={this.handleGenerateVoiceTracks}
          onCombineReel={this.downloadCombinedReel}
          onDownloadAll={this.downloadAllImages}
          onDownloadCombinedVoice={this.downloadCombinedVoiceTracks}
          onSave={this.saveSegments}
          onClear={this.handleClear}
          qualityScores={this.state.qualityScores}
          qualityScoreStatus={this.state.qualityScoreStatus}
          onRewriteDimension={this.handleRewriteDimension}
        />
      );
  }
}

export default ViralReelCreator;
